import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import sharepointConfig from '../config/sharepointConfig';
import { fmtDatum, berekenVerlofUrenHeleDag, berekenUrenMetPauze } from '../utils/datum';
import { getAzureConfig, saveAzureConfig, clearAzureConfig, loadAzureConfigFromFirestore } from '../config/azureConfig';
import { connectOutlook, disconnectOutlook, getOutlookStatus, testConnection, purgeAllCalendarEvents, syncBeschikbaarheidToOutlook, purgeWerkroosterEvents, getOutlookStatusSync, autoSyncWerkrooster, purgeMutatieEvents, syncBulkMutaties } from '../services/calendarService';
import { genereerMedewerkerPDF, genereerTotaaloverzichtPDF } from '../utils/pdfExport';
import { exportData, leesImportBestand, exportMedewerkersCSV, exportVerlofCSV, exportOverurenCSV, exportFlexPlanningCSV, parseVerlofCSV, parseOverurenCSV, parseFlexPlanningCSV, leesCSVBestand, downloadTemplate } from '../utils/importExport';
import { batchImportVerlof, batchImportOveruren, batchImportFlexPlanning, updateVerlofaanvraag, updateOveruren, updateFlexPlanning } from '../services/dataService';
import { downloadBackup, herstelBackup } from '../utils/backup';
import { valideerWachtwoord } from '../utils/wachtwoord';
import SystemCheck from './SystemCheck';
import BeschikbaarheidDashboard from './BeschikbaarheidDashboard';

// Pro-rata preview berekening (weekuren × 5 = jaarverlofuren)
const VERLOF_FACTOR = 5;
function berekenProRataPreview(contractUrenWeek, indienstDatum, contractWijzigingen, jaar) {
  if (!contractUrenWeek || !jaar) return 0;
  const jaarStart = new Date(jaar, 0, 1);
  const jaarEind = new Date(jaar, 11, 31);
  const dagenInJaar = (jaarEind - jaarStart) / (1000 * 60 * 60 * 24) + 1;
  const verlofUrenJaar = contractUrenWeek * VERLOF_FACTOR;
  let effectieveStart = jaarStart;
  if (indienstDatum) {
    const indienst = new Date(indienstDatum);
    if (indienst > jaarEind) return 0;
    if (indienst > jaarStart) effectieveStart = indienst;
  }
  const wijzigingen = (contractWijzigingen || [])
    .filter(w => w.ingangsDatum && w.contractUren !== undefined)
    .sort((a, b) => a.ingangsDatum.localeCompare(b.ingangsDatum));
  const periodes = [];
  let huidigeVerlof = verlofUrenJaar;
  let huidigStart = effectieveStart;
  for (const w of wijzigingen) {
    const wijzigDatum = new Date(w.ingangsDatum);
    if (wijzigDatum <= effectieveStart) { huidigeVerlof = (w.contractUren || 0) * VERLOF_FACTOR; continue; }
    if (wijzigDatum > jaarEind) break;
    const periodeEind = new Date(wijzigDatum);
    periodeEind.setDate(periodeEind.getDate() - 1);
    if (periodeEind >= huidigStart) periodes.push({ start: huidigStart, eind: periodeEind, uren: huidigeVerlof });
    huidigStart = wijzigDatum;
    huidigeVerlof = (w.contractUren || 0) * VERLOF_FACTOR;
  }
  periodes.push({ start: huidigStart, eind: jaarEind, uren: huidigeVerlof });
  let totaal = 0;
  for (const p of periodes) {
    const dagen = (p.eind - p.start) / (1000 * 60 * 60 * 24) + 1;
    totaal += p.uren * (dagen / dagenInJaar);
  }
  return Math.round(totaal * 10) / 10;
}

const DAGEN = ['ma','di','wo','do','vr'];
const DAG_LABELS = { ma: 'Maandag', di: 'Dinsdag', wo: 'Woensdag', do: 'Donderdag', vr: 'Vrijdag' };
const LEEG_ROOSTER = () => {
  const r = {};
  DAGEN.forEach(d => { r[d] = { ochtendStart: '', ochtendEind: '', middagStart: '', middagEind: '', avondStart: '', avondEind: '' }; });
  return r;
};

// Convert old format "08:00-12:30" to new split format
function parseDeel(val) {
  if (!val) return { start: '', eind: '' };
  const parts = val.split('-');
  return { start: parts[0] || '', eind: parts[1] || '' };
}
function roosterToForm(rooster) {
  if (!rooster) return LEEG_ROOSTER();
  const form = {};
  DAGEN.forEach(d => {
    const dag = rooster[d] || {};
    const och = parseDeel(dag.ochtend);
    const mid = parseDeel(dag.middag);
    const avo = parseDeel(dag.avond);
    form[d] = { ochtendStart: och.start, ochtendEind: och.eind, middagStart: mid.start, middagEind: mid.eind, avondStart: avo.start, avondEind: avo.eind };
  });
  return form;
}
function formToRooster(form) {
  const rooster = {};
  DAGEN.forEach(d => {
    const f = form[d];
    rooster[d] = {
      ochtend: (f.ochtendStart && f.ochtendEind) ? `${f.ochtendStart}-${f.ochtendEind}` : '',
      middag: (f.middagStart && f.middagEind) ? `${f.middagStart}-${f.middagEind}` : '',
      avond: (f.avondStart && f.avondEind) ? `${f.avondStart}-${f.avondEind}` : '',
    };
  });
  return rooster;
}

function AdminPanel() {
  const {
    isAdmin, isManager, currentUser, medewerkers, verlofaanvragen, overuren, feestdagen, vakantiePerioden, roosterWijzigingen, jaararchief,
    medewerkerKleurMap,
    flexPlanning, flexUren, flexUitbetalingen,
    verlofTypes, voegVerlofTypeToe, werkVerlofTypeBij, verwijderVerlofType,
    werkMedewerkerBij, voegMedewerkerToe, verwijderMedewerker, verwijderMedewerkerCompleet, resetMedewerkerData, wijzigWachtwoord,
    voegFeestdagToe, verwijderFeestdag, voerJaarafsluitingUit,
    voegVakantiePeriodeToe, verwijderVakantiePeriode,
    voegRoosterWijzigingToe, verwijderRoosterWijziging,
    betaalFlexUren, indienVerlofaanvraag, updateOverurenStatus,
    menuConfig, saveMenuConfig,
  } = useApp();

  // Manager-only modus: alleen roosterwijzigingen tab
  const managerOnly = isManager && !isAdmin;

  const location = useLocation();
  const [activeTab, setActiveTab] = useState(managerOnly ? 'roosterwijzigingen' : 'overzicht');

  // Handle tab navigation from other pages (e.g., admin hub)
  useEffect(() => {
    if (location.state?.tab) {
      setActiveTab(location.state.tab);
      // Clear state to prevent re-triggering
      window.history.replaceState({}, '');
    }
  }, [location.state]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [newMedewerker, setNewMedewerker] = useState(null);
  const [newFeestdag, setNewFeestdag] = useState({ datum: '', naam: '' });
  const [newVakantiePeriode, setNewVakantiePeriode] = useState({ naam: '', startDatum: '', eindDatum: '' });
  const [newRoosterWijziging, setNewRoosterWijziging] = useState({ medewerkerId: '', datum: '', heleDag: true, startTijd: '', eindTijd: '', omschrijving: '', vervangDatum: '' });
  const [processing, setProcessing] = useState(false);
  // Verloftype beheer
  const [newVerlofType, setNewVerlofType] = useState(null);
  const [editVerlofTypeId, setEditVerlofTypeId] = useState(null);
  const [editVerlofTypeForm, setEditVerlofTypeForm] = useState({});
  const [roosterEditId, setRoosterEditId] = useState(null);
  const [roosterForm, setRoosterForm] = useState(LEEG_ROOSTER());
  // Flex saldo aanpassing
  const [saldoForm, setSaldoForm] = useState({ categorie: 'verlofuren', medewerkerId: '', uren: '', type: 'uitbetaling', omschrijving: '' });
  // Contract wijziging
  const [contractWijzForm, setContractWijzForm] = useState({ medewerkerId: '', ingangsDatum: '', contractUren: '' });
  const [terugInDienstForm, setTerugInDienstForm] = useState(null); // { mId, datum }
  const importRef = useRef(null);
  const csvImportRef = useRef(null);
  const [importStatus, setImportStatus] = useState(null);
  const [csvImport, setCsvImport] = useState(null); // { type, records, warnings, errors }
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvImportResult, setCsvImportResult] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { mId, stap: 1|2 }
  const [resetConfirm, setResetConfirm] = useState(null); // { mId, naam, bezig, resultaat }
  const [wwResetForm, setWwResetForm] = useState(null); // { mId, naam, nieuw: '', bevestig: '' }
  const [pdfDialog, setPdfDialog] = useState(null); // { vanDatum, totDatum, medewerkerId: 'alle' | id }

  // Beheerder protection: count how many beheerders there are
  const aantalBeheerders = useMemo(() =>
    medewerkers.filter(m => m.IsAdmin && (m.Status || 'actief') === 'actief').length
  , [medewerkers]);

  // Auto-sync werkrooster naar Outlook bij wijzigingen (background, non-blocking)
  const triggerOutlookWerkroosterSync = () => {
    try {
      const { connected } = getOutlookStatusSync();
      if (!connected) return;
      // Async in background — purge + re-sync werkrooster
      autoSyncWerkrooster({
        medewerkers, verlofaanvragen, roosterWijzigingen, feestdagen,
        medewerkerKleurMap,
      });
    } catch { /* Outlook niet beschikbaar */ }
  };

  // WhatsApp uitnodiging sturen — ook vanuit ww-reset modal bruikbaar
  const stuurWhatsAppUitnodiging = (m, tijdelijkWw = null) => {
    const tel = (m.Telefoon || m.telefoon || '').replace(/[\s\-\(\)]/g, '');
    const url = 'https://verlofapp.slaaploods.nl';
    const voornaam = (m.Naam || m.naam || '').split(' ')[0];
    const email = m.Email || m.email || '';
    // Als geen ww meegegeven, vraag het aan de admin
    let ww = tijdelijkWw;
    if (!ww) {
      ww = window.prompt(`Wachtwoord voor ${m.Naam || m.naam}?\n\n(Typ het wachtwoord dat je hebt ingesteld, of stel er eerst een in via 🔑)`);
      if (!ww) return; // admin annuleert
    }
    const msg =
      `Hoi ${voornaam}, welkom bij Slaaploods! 🛏️\n\n` +
      `Je hebt een account voor onze *Verlofbeheer app*. Hiermee regel je verlof, ziekmeldingen en overuren rechtstreeks vanaf je telefoon.\n\n` +
      `*Inloggegevens:*\n` +
      `🔗 ${url}\n` +
      `📧 ${email}\n` +
      `🔑 Wachtwoord: *${ww}*\n\n` +
      `⚠️ *Wijzig je wachtwoord direct na de eerste login.*\n\n` +
      `*App installeren (aanbevolen):*\n` +
      `📱 iPhone (Safari): tik op ⎙ → "Zet op beginscherm"\n` +
      `📱 Android (Chrome): tik op ⋮ → "Aan beginscherm toevoegen"\n\n` +
      `Als je nog vragen hebt, neem contact op met de beheerder: Willem Reichardt.\n` +
      `— Slaaploods.nl`;
    if (tel) {
      const nr = tel.startsWith('06') ? '31' + tel.slice(1) : tel.startsWith('+') ? tel.slice(1) : tel;
      window.open(`https://wa.me/${nr}?text=${encodeURIComponent(msg)}`, '_blank');
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
    }
  };

  // Outlook agenda sync
  const [azureForm, setAzureForm] = useState(() => getAzureConfig() || { clientId: '', tenantId: '' });
  const [outlookStatus, setOutlookStatus] = useState({ connected: false, loading: true });
  const [outlookMsg, setOutlookMsg] = useState('');

  // Laad Azure config uit Firestore als localStorage leeg is (andere PC/browser)
  useEffect(() => {
    if (!azureForm.clientId || !azureForm.tenantId) {
      loadAzureConfigFromFirestore().then(config => {
        if (config?.clientId && config?.tenantId) {
          setAzureForm(config);
        }
      });
    }
  }, []); // only on mount

  const checkOutlookStatus = useCallback(async () => {
    try {
      const status = await getOutlookStatus();
      setOutlookStatus({ ...status, loading: false });
    } catch { setOutlookStatus({ connected: false, loading: false }); }
  }, []);

  useEffect(() => {
    if (azureForm.clientId && azureForm.tenantId) checkOutlookStatus();
    else setOutlookStatus({ connected: false, loading: false });
  }, [azureForm.clientId, azureForm.tenantId, checkOutlookStatus]);

  const handleAzureSave = async () => {
    if (!azureForm.clientId || !azureForm.tenantId) return;
    await saveAzureConfig(azureForm);
    setOutlookMsg('✅ Azure configuratie opgeslagen (ook voor andere apparaten)');
    setTimeout(() => setOutlookMsg(''), 3000);
  };

  const handleOutlookConnect = async () => {
    setOutlookMsg('⏳ Doorsturen naar Microsoft login...');
    try {
      const result = await connectOutlook();
      // Als er al een account was (bijv. na redirect), direct verbonden tonen
      if (result.success) {
        setOutlookStatus({ connected: true, account: result.account, name: result.name, loading: false });
        setOutlookMsg(`✅ Verbonden als ${result.account}`);
        // Agenda opzoeken op achtergrond
        try {
          const status = await getOutlookStatus();
          if (status.connected) {
            setOutlookStatus(prev => ({ ...prev, calendarName: status.calendarName }));
          }
        } catch { /* niet kritiek */ }
      } else if (result.error) {
        // Alleen tonen als het een echte fout is (niet "redirect gestart")
        if (!result.error.includes('Redirect')) {
          setOutlookMsg(`❌ ${result.error}`);
        }
        // Bij redirect navigeert de browser weg — deze code wordt niet bereikt
      }
    } catch (err) {
      // Bij redirect navigeert de browser weg, dit punt wordt niet bereikt
      console.log('[Outlook] Redirect gestart of fout:', err);
    }
  };

  const handleOutlookDisconnect = async () => {
    await disconnectOutlook();
    setOutlookStatus({ connected: false, loading: false });
    setOutlookMsg('Verbinding verbroken');
  };

  const handleOutlookTest = async () => {
    setOutlookMsg('Testen...');
    const result = await testConnection();
    if (result.success) setOutlookMsg(`✅ Verbonden: ${result.name} (${result.email})`);
    else setOutlookMsg(`❌ ${result.error}`);
  };

  // Outlook full sync
  const [syncProgress, setSyncProgress] = useState(null); // { current, total, item }
  const [syncResult, setSyncResult] = useState(null);

  // Werkrooster sync naar Outlook (met periode keuze)
  const handleWerkroosterSync = async (totEindStr, label) => {
    const maanden = Math.round((new Date(totEindStr) - new Date()) / (1000 * 60 * 60 * 24 * 30));
    if (!window.confirm(`Werkrooster synchroniseren naar Outlook t/m ${new Date(totEindStr).toLocaleDateString('nl-NL')} (~${maanden} maanden vooruit).\n\nPer medewerker per werkdag wordt een event aangemaakt.\n\nDoorgaan?`)) return;
    setSyncResult(null);
    setSyncProgress({ current: 0, total: 0, item: { actie: 'oude werkrooster-events verwijderen...' } });
    try {
      const purged = await purgeWerkroosterEvents(({ fase, verwijderd, huidig }) => {
        setSyncProgress({ current: verwijderd || 0, total: 0, item: { actie: `${fase}: ${verwijderd} verwijderd` } });
      });

      const mappedVerlof = verlofaanvragen.map(v => ({
        ...v, id: v.Id, status: v.Status, type: v.Type,
        startDatum: v.StartDatum, eindDatum: v.EindDatum,
        medewerkerId: v.medewerkerId,
        medewerkerNaam: v.MedewerkerNaam,
        dagdeel: v.Dagdeel, uren: v.Uren,
        isOpenZiek: v.isOpenZiek,
      }));

      setSyncProgress({ current: 0, total: 0, item: { actie: 'werkrooster synchroniseren...' } });
      const stats = await syncBeschikbaarheidToOutlook({
        medewerkers,
        verlofaanvragen: mappedVerlof,
        roosterWijzigingen,
        feestdagen,
        medewerkerKleurMap,
        totEind: totEindStr,
        onProgress: ({ teller, total, naam, datum, subject }) => {
          setSyncProgress({ current: teller, total, item: { naam, actie: datum, type: subject?.substring(0, 40) } });
        },
      });
      setSyncProgress(null);
      setSyncResult({ created: stats.created, updated: 0, deleted: 0, synced: stats.created, errors: stats.errors, purged, werkrooster: true, label });
    } catch (err) {
      console.error('Werkrooster sync mislukt:', err);
      alert('Sync mislukt: ' + err.message);
      setSyncProgress(null);
      setSyncResult({ error: err.message });
    }
  };

  // Mutatie sync (verlof + overuren + flex) naar Outlook
  const handleMutatieSync = async (totEindStr, label) => {
    const vanDatum = new Date().toISOString().split('T')[0];
    const maanden = Math.round((new Date(totEindStr) - new Date()) / (1000 * 60 * 60 * 24 * 30));
    if (!window.confirm(`Alle mutaties (verlof, overuren, flex) synchroniseren naar Outlook van vandaag t/m ${new Date(totEindStr).toLocaleDateString('nl-NL')} (~${maanden} maanden).\n\nBestaande mutatie-events worden eerst verwijderd.\n\nDoorgaan?`)) return;
    setSyncResult(null);
    setSyncProgress({ current: 0, total: 0, item: { actie: 'mutatie-events opruimen...' } });
    try {
      // Stap 1: Purge bestaande mutatie-events
      const mappedVerlof = verlofaanvragen.map(v => ({
        ...v, id: v.Id, calendarEventId: v.calendarEventId,
        startDatum: v.StartDatum, medewerkerId: v.medewerkerId, medewerkerNaam: v.MedewerkerNaam,
      }));
      const purged = await purgeMutatieEvents({
        verlofaanvragen: mappedVerlof, overuren, flexPlanning,
        onProgress: ({ fase, verwijderd }) => {
          setSyncProgress({ current: verwijderd || 0, total: 0, item: { actie: `${fase}: ${verwijderd || 0} verwijderd` } });
        },
      });

      // Stap 2: Wis calendarEventIds in Firebase
      setSyncProgress({ current: 0, total: 0, item: { actie: 'calendarEventIds opschonen...' } });
      for (const v of verlofaanvragen.filter(x => x.calendarEventId)) {
        try { await updateVerlofaanvraag(v.Id, { calendarEventId: null }); } catch {}
      }
      for (const o of overuren.filter(x => x.calendarEventId)) {
        try { await updateOveruren(o.id, { calendarEventId: null }); } catch {}
      }
      for (const f of flexPlanning.filter(x => x.calendarEventId)) {
        try { await updateFlexPlanning(f.id, { calendarEventId: null }); } catch {}
      }

      // Stap 3: Pauze voor rate limiting
      await new Promise(r => setTimeout(r, 1500));

      // Stap 4: Sync alle mutaties in periode
      setSyncProgress({ current: 0, total: 0, item: { actie: 'mutaties synchroniseren...' } });
      const mappedVerlofSync = verlofaanvragen.map(v => ({
        ...v, id: v.Id, status: v.Status, type: v.Type,
        startDatum: v.StartDatum, eindDatum: v.EindDatum,
        medewerkerId: v.medewerkerId, medewerkerNaam: v.MedewerkerNaam,
        dagdeel: v.Dagdeel, uren: v.Uren, isOpenZiek: v.isOpenZiek,
        calendarEventId: null, // gewist
      }));

      const stats = await syncBulkMutaties({
        verlofaanvragen: mappedVerlofSync, overuren, flexPlanning,
        verlofTypes, medewerkers, medewerkerKleurMap,
        vanDatum, totDatum: totEindStr,
        onProgress: ({ teller, total, item }) => {
          setSyncProgress({ current: teller, total, item: { actie: item } });
        },
        updateVerlofaanvraagFn: updateVerlofaanvraag,
        updateOverurenFn: updateOveruren,
        updateFlexPlanningFn: updateFlexPlanning,
      });
      setSyncProgress(null);
      setSyncResult({ verlof: stats.verlof, overuren: stats.overuren, flex: stats.flex, errors: stats.errors, purged, mutaties: true, label });
    } catch (err) {
      console.error('Mutatie sync mislukt:', err);
      alert('Sync mislukt: ' + err.message);
      setSyncProgress(null);
      setSyncResult({ error: err.message });
    }
  };

  // Alles verwijderen (werkrooster + mutaties)
  const handleDeleteAll = async () => {
    if (!window.confirm('⚠️ ALLE Outlook agenda-events worden verwijderd:\n• Werkrooster\n• Verlof\n• Overuren\n• Flex planning\n\nDit kan niet ongedaan worden. Doorgaan?')) return;
    setSyncResult(null);
    setSyncProgress({ current: 0, total: 0, item: { actie: 'alle events verwijderen...' } });
    try {
      const purged = await purgeAllCalendarEvents(({ fase, verwijderd, huidig }) => {
        setSyncProgress({ current: verwijderd || 0, total: 0, item: { actie: `${fase}: ${verwijderd} verwijderd${huidig ? ` (${huidig.substring(0, 30)})` : ''}` } });
      });

      // Wis alle calendarEventIds in Firebase
      setSyncProgress({ current: 0, total: 0, item: { actie: 'calendarEventIds opschonen...' } });
      for (const v of verlofaanvragen.filter(x => x.calendarEventId)) {
        try { await updateVerlofaanvraag(v.Id, { calendarEventId: null }); } catch {}
      }
      for (const o of overuren.filter(x => x.calendarEventId)) {
        try { await updateOveruren(o.id, { calendarEventId: null }); } catch {}
      }
      for (const f of flexPlanning.filter(x => x.calendarEventId)) {
        try { await updateFlexPlanning(f.id, { calendarEventId: null }); } catch {}
      }

      setSyncProgress(null);
      setSyncResult({ purged, deleteAll: true });
    } catch (err) {
      console.error('Alles verwijderen mislukt:', err);
      alert('Verwijderen mislukt: ' + err.message);
      setSyncProgress(null);
      setSyncResult({ error: err.message });
    }
  };

  const handleContractWijziging = async () => {
    if (!contractWijzForm.medewerkerId || !contractWijzForm.ingangsDatum || !contractWijzForm.contractUren) return;
    const mw = medewerkers.find(m => m.Id === contractWijzForm.medewerkerId);
    if (!mw) return;
    setProcessing(true);
    try {
      const bestaandeWijzigingen = mw.ContractWijzigingen || [];
      const nieuweWijziging = {
        ingangsDatum: contractWijzForm.ingangsDatum,
        contractUren: Number(contractWijzForm.contractUren),
        vorigeUren: mw.ContractUren,
        datum: new Date().toISOString().split('T')[0],
      };
      await werkMedewerkerBij(mw.Id, {
        contractUren: Number(contractWijzForm.contractUren),
        contractWijzigingen: [...bestaandeWijzigingen, nieuweWijziging],
      });
      setContractWijzForm({ medewerkerId: '', ingangsDatum: '', contractUren: '' });
      // Auto-sync naar Outlook als verbonden
      triggerOutlookWerkroosterSync();
    } catch (err) { console.error(err); } finally { setProcessing(false); }
  };

  if (!isAdmin && !isManager) {
    return (
      <div style={styles.noAccess}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <h2>Geen toegang</h2>
        <p>Je hebt geen beheerdersrechten.</p>
      </div>
    );
  }

  const currentYear = new Date().getFullYear();
  const openVerlof = verlofaanvragen.filter(v => v.Status === 'ingediend').length;
  const openOveruren = overuren.filter(o => o.Status === 'ingediend').length;
  const goedgekeurdVerlof = verlofaanvragen.filter(v => v.Status === 'goedgekeurd').length;
  const flexkrachten = medewerkers.filter(m => m.Rol === 'flexkracht');

  const allTabs = [
    { key: 'overzicht', label: '📊 Overzicht' },
    { key: 'medewerkers', label: '👥 Medewerkers' },
    { key: 'verloftypen', label: '🏷️ Verloftypen' },
    { key: 'werkrooster', label: '🕐 Werkrooster' },
    { key: 'uitbetalen', label: '💰 Uitbetalen' },
    { key: 'feestdagen', label: '📅 Feestdagen' },
    { key: 'vakantieperioden', label: '🏖️ Vakantieperioden' },
    { key: 'roosterwijzigingen', label: '🔄 Roosterwijzigingen' },
    { key: 'archief', label: '📁 Archief' },
    { key: 'menu', label: '📋 Menu' },
    { key: 'instellingen', label: '⚙️ Instellingen' },
    { key: 'beschikbaarheid', label: '👥 Beschikbaarheid' },
    { key: 'diagnose', label: '🩺 Diagnose' },
  ];
  // Manager ziet roosterwijzigingen + flex planning
  const managerTabs = ['roosterwijzigingen', 'werkrooster', 'medewerkers'];
  const tabs = managerOnly
    ? allTabs.filter(t => managerTabs.includes(t.key))
    : allTabs;

  // ---- Medewerker bewerken ----
  const startEdit = (m) => {
    setEditingId(m.Id);
    setEditForm({
      naam: m.Naam, email: m.Email, afdeling: m.Afdeling,
      verkorteNaam: m.VerkorteNaam || '',
      kleur: m.Kleur || '',
      isManager: m.IsManager ?? false, isAdmin: m.IsAdmin ?? false,
      isFlexkracht: m.Rol === 'flexkracht',
      contractUren: m.ContractUren || 200, meegenomenUren: m.MeegenomenUren || 0,
      indienstDatum: m.IndienstDatum || '',
      flexEnabled: m.FlexEnabled ?? false,
      heeftFlexProfiel: m.HeeftFlexProfiel ?? false,
      weekFlexUren: m.WeekFlexUren ?? 0,
      telefoon: m.Telefoon || '',
      showroomMedewerker: m.ShowroomMedewerker ?? false,
      zaterdagDrempel: m.ZaterdagDrempel ?? 0,
      wachtwoord: '', // leeg = niet wijzigen
    });
  };

  const saveEdit = async () => {
    // Protect last beheerder
    const currentMw = medewerkers.find(m => m.Id === editingId);
    if (currentMw?.IsAdmin && !editForm.isAdmin && aantalBeheerders <= 1) {
      alert('Er moet minimaal 1 actieve beheerder zijn. Maak eerst iemand anders beheerder.');
      return;
    }
    setProcessing(true);
    try {
      const isFk = editForm.isFlexkracht;
      const updates = {
        naam: editForm.naam, email: editForm.email, afdeling: editForm.afdeling,
        verkorteNaam: editForm.verkorteNaam || '',
        kleur: editForm.kleur || '',
        rol: isFk ? 'flexkracht' : 'medewerker',
        isManager: isFk ? false : (editForm.isManager ?? false),
        isAdmin: isFk ? false : (editForm.isAdmin ?? false),
        contractUren: isFk ? 0 : Number(editForm.contractUren),
        meegenomenUren: isFk ? 0 : Number(editForm.meegenomenUren),
        indienstDatum: editForm.indienstDatum || null,
        flexEnabled: isFk ? false : editForm.flexEnabled,
        heeftFlexProfiel: editForm.heeftFlexProfiel ?? false,
        weekFlexUren: isFk ? 0 : (Number(editForm.weekFlexUren) || 0),
        telefoon: editForm.telefoon || '',
        showroomMedewerker: isFk ? false : (editForm.showroomMedewerker ?? false),
        zaterdagDrempel: isFk ? 0 : (Number(editForm.zaterdagDrempel) || 0),
      };
      if (editForm.wachtwoord) updates.wachtwoord = editForm.wachtwoord;
      await werkMedewerkerBij(editingId, updates);
      setEditingId(null);
    } catch (err) { console.error(err); } finally { setProcessing(false); }
  };

  const startNewMedewerker = () => {
    setNewMedewerker({
      naam: '', email: '', wachtwoord: '', afdeling: '', verkorteNaam: '', kleur: '',
      isFlexkracht: false, isManager: false, isAdmin: false,
      contractUren: 40, indienstDatum: new Date().toISOString().split('T')[0],
      flexEnabled: false, heeftFlexProfiel: false, telefoon: '', weekFlexUren: 0,
      showroomMedewerker: false, zaterdagDrempel: 0,
    });
  };

  const saveNewMedewerker = async () => {
    if (!newMedewerker.naam || !newMedewerker.email || !newMedewerker.wachtwoord) return;
    const wwCheck = valideerWachtwoord(newMedewerker.wachtwoord);
    if (!wwCheck.valid) { alert('Wachtwoord voldoet niet:\n• ' + wwCheck.errors.join('\n• ')); return; }
    setProcessing(true);
    try {
      await voegMedewerkerToe({
        ...newMedewerker,
        rol: newMedewerker.isFlexkracht ? 'flexkracht' : 'medewerker',
        isManager: newMedewerker.isFlexkracht ? false : (newMedewerker.isManager ?? false),
        isAdmin: newMedewerker.isFlexkracht ? false : (newMedewerker.isAdmin ?? false),
        contractUren: newMedewerker.isFlexkracht ? 0 : (newMedewerker.contractUren || 0),
        flexEnabled: newMedewerker.isFlexkracht ? false : (newMedewerker.flexEnabled ?? false),
        weekFlexUren: newMedewerker.isFlexkracht ? 0 : (newMedewerker.weekFlexUren || 0),
        meegenomenUren: newMedewerker.isFlexkracht ? 0 : (newMedewerker.meegenomenUren || 0),
        heeftFlexProfiel: newMedewerker.heeftFlexProfiel ?? false,
        showroomMedewerker: newMedewerker.isFlexkracht ? false : (newMedewerker.showroomMedewerker ?? false),
        zaterdagDrempel: newMedewerker.isFlexkracht ? 0 : (Number(newMedewerker.zaterdagDrempel) || 0),
        mustChangePassword: true,   // dwing ww-wijziging af bij eerste login
      });
      setNewMedewerker(null);
    } catch (err) { console.error(err); } finally { setProcessing(false); }
  };

  // Status wijzigen (actief/inactief/uitdienst)
  const wijzigStatus = async (mId, nieuweStatus, extraData = {}) => {
    // Protect last beheerder from deactivation
    if (nieuweStatus !== 'actief') {
      const mw = medewerkers.find(m => m.Id === mId);
      if (mw?.IsAdmin && aantalBeheerders <= 1) {
        alert('Laatste beheerder kan niet gedeactiveerd worden. Maak eerst iemand anders beheerder.');
        return;
      }
    }
    if (!window.confirm(
      nieuweStatus === 'inactief' ? 'Medewerker inactief zetten? Account wordt tijdelijk geblokkeerd.'
      : nieuweStatus === 'uitdienst' ? 'Medewerker uit dienst melden? Account wordt geblokkeerd.'
      : 'Medewerker weer activeren?'
    )) return;
    setProcessing(true);
    try {
      const updates = { status: nieuweStatus, ...extraData };
      if (nieuweStatus === 'uitdienst') updates.uitDienstDatum = extraData.uitDienstDatum || new Date().toISOString().split('T')[0];
      if (nieuweStatus === 'actief' && extraData.indienstDatum) updates.indienstDatum = extraData.indienstDatum;
      await werkMedewerkerBij(mId, updates);
    } catch (err) { console.error(err); } finally { setProcessing(false); }
  };

  // ---- Werkrooster (FIXED: proper individual time fields) ----
  const startRoosterEdit = (m) => {
    setRoosterEditId(m.Id);
    setRoosterForm(roosterToForm(m.Werkrooster));
  };

  const saveRooster = async () => {
    setProcessing(true);
    try {
      await werkMedewerkerBij(roosterEditId, { werkrooster: formToRooster(roosterForm) });
      setRoosterEditId(null);
      // Auto-sync naar Outlook als verbonden
      triggerOutlookWerkroosterSync();
    } catch (err) { console.error(err); } finally { setProcessing(false); }
  };

  const updateRoosterVeld = (dag, veld, value) => {
    setRoosterForm(prev => ({ ...prev, [dag]: { ...prev[dag], [veld]: value } }));
  };

  const clearRoosterDeel = (dag, deel) => {
    setRoosterForm(prev => ({
      ...prev,
      [dag]: { ...prev[dag], [`${deel}Start`]: '', [`${deel}Eind`]: '' }
    }));
  };

  // ---- Feestdag ----
  const handleAddFeestdag = async () => {
    if (!newFeestdag.datum || !newFeestdag.naam) return;
    await voegFeestdagToe(newFeestdag);
    setNewFeestdag({ datum: '', naam: '' });
  };

  const handleAddVakantiePeriode = async () => {
    if (!newVakantiePeriode.naam || !newVakantiePeriode.startDatum || !newVakantiePeriode.eindDatum) return;
    await voegVakantiePeriodeToe(newVakantiePeriode);
    setNewVakantiePeriode({ naam: '', startDatum: '', eindDatum: '' });
  };

  const handleAddRoosterWijziging = async () => {
    if (!newRoosterWijziging.medewerkerId || !newRoosterWijziging.datum) return;
    if (!newRoosterWijziging.heleDag && (!newRoosterWijziging.startTijd || !newRoosterWijziging.eindTijd)) return;
    const mw = medewerkers.find(m => m.Id === newRoosterWijziging.medewerkerId);
    await voegRoosterWijzigingToe({
      ...newRoosterWijziging,
      medewerkerNaam: mw?.Naam || '',
    });
    setNewRoosterWijziging({ medewerkerId: '', datum: '', heleDag: true, startTijd: '', eindTijd: '', omschrijving: '', vervangDatum: '' });
  };

  // ---- Jaarafsluiting ----
  const handleJaarafsluiting = async () => {
    const jaar = currentYear - 1;
    if (!window.confirm(`Weet je zeker dat je de jaarafsluiting voor ${jaar} wilt uitvoeren?`)) return;
    setProcessing(true);
    try { await voerJaarafsluitingUit(jaar); } catch (err) { console.error(err); } finally { setProcessing(false); }
  };

  // ---- Uitbetaling / correctie ----
  const handleSaldoAanpassing = async () => {
    if (!saldoForm.medewerkerId || !saldoForm.uren) return;
    setProcessing(true);
    try {
      const mw = medewerkers.find(m => m.Id === saldoForm.medewerkerId);
      const fk = flexkrachten.find(f => f.Id === saldoForm.medewerkerId);
      const naam = mw?.Naam || fk?.Naam || 'Onbekend';
      const uren = Number(saldoForm.uren);

      if (saldoForm.categorie === 'flex') {
        await betaalFlexUren({
          flexkrachtId: saldoForm.medewerkerId,
          flexkrachtNaam: naam,
          uren,
          omschrijving: saldoForm.omschrijving || `Saldo ${saldoForm.type}`,
          type: saldoForm.type,
        });
      } else if (saldoForm.categorie === 'overuren') {
        // Overuren uitbetalen — markeer goedgekeurde als uitbetaald
        const todayStr = new Date().toISOString().split('T')[0];
        const goedgekeurde = overuren
          .filter(o => o.medewerkerId === saldoForm.medewerkerId && o.Status === 'goedgekeurd' && (o.datum || '') <= todayStr)
          .sort((a, b) => (a.datum || '').localeCompare(b.datum || ''));
        let resterend = Math.abs(uren);
        for (const ou of goedgekeurde) {
          if (resterend <= 0) break;
          await updateOverurenStatus(ou.Id, 'uitbetaald');
          resterend -= (ou.Uren || 0);
        }
        await betaalFlexUren({
          flexkrachtId: saldoForm.medewerkerId,
          flexkrachtNaam: naam + ' (overuren)',
          uren,
          omschrijving: saldoForm.omschrijving || `Overuren ${saldoForm.type}`,
          type: saldoForm.type === 'correctie' ? 'correctie' : 'overuren-uitbetaling',
        });
      } else if (saldoForm.categorie === 'verlofuren') {
        // Verlofuren uitbetalen — reduceer meegenomenUren
        if (mw) {
          const huidigMeegen = mw.MeegenomenUren || mw.meegenomenUren || 0;
          await werkMedewerkerBij(mw.Id, { meegenomenUren: huidigMeegen - Math.abs(uren) });
        }
        await betaalFlexUren({
          flexkrachtId: saldoForm.medewerkerId,
          flexkrachtNaam: naam + ' (verlofuren)',
          uren,
          omschrijving: saldoForm.omschrijving || `Verlofuren ${saldoForm.type}`,
          type: saldoForm.type === 'correctie' ? 'correctie' : 'verlofuren-uitbetaling',
        });
      }
      setSaldoForm({ categorie: saldoForm.categorie, medewerkerId: '', uren: '', type: 'uitbetaling', omschrijving: '' });
    } catch (err) { console.error(err); alert('Fout: ' + err.message); } finally { setProcessing(false); }
  };

  // Flex saldi berekenen
  const flexSaldi = flexkrachten.map(fk => {
    const ingepland = flexPlanning.filter(p => p.flexkrachtId === fk.Id && new Date(p.datum).getFullYear() === currentYear).reduce((s, p) => s + (p.uren || 0), 0);
    const gewerkt = flexUren.filter(u => u.flexkrachtId === fk.Id && new Date(u.datum).getFullYear() === currentYear).reduce((s, u) => s + (u.uren || 0), 0);
    const uitbetaald = flexUitbetalingen.filter(u => u.flexkrachtId === fk.Id && new Date(u.datum).getFullYear() === currentYear).reduce((s, u) => s + (u.uren || 0), 0);
    return { ...fk, ingepland, gewerkt, uitbetaald, openstaand: gewerkt - uitbetaald };
  });

  const rolInfo = (rol) => sharepointConfig.rollen.find(r => r.key === rol) || {};

  // Helper: time input with clear
  const TimeInput = ({ value, onChange, onClear }) => (
    <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      <input type="time" style={{ ...styles.editInput, width: 100, padding: '4px 6px', fontSize: 12 }}
        value={value} onChange={e => onChange(e.target.value)} />
      {value && <button style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, color: '#D13438', padding: 2 }}
        onClick={onClear} title="Wissen">✕</button>}
    </div>
  );

  const [showTabMenu, setShowTabMenu] = useState(false);

  return (
    <div style={styles.container}>
      {/* Mobile: grid menu like bottom nav */}
      <div className="r-admin-tab-grid" style={{ display: 'none' }}>
        <button className="r-admin-tab-toggle" onClick={() => setShowTabMenu(!showTabMenu)}
          style={{ width: '100%', padding: '10px 16px', background: '#fff', border: '1px solid #edebe9', borderRadius: 10,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14, fontWeight: 600, color: '#323130', cursor: 'pointer' }}>
          <span>{tabs.find(t => t.key === activeTab)?.label || 'Menu'}</span>
          <span style={{ fontSize: 12, color: '#605e5c' }}>{showTabMenu ? '▲' : '▼'}</span>
        </button>
        {showTabMenu && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginTop: 8, background: '#fff', borderRadius: 12, padding: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            {tabs.map(tab => (
              <button key={tab.key} onClick={() => { setActiveTab(tab.key); setShowTabMenu(false); }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '10px 4px',
                  borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 600, lineHeight: 1.3,
                  background: activeTab === tab.key ? '#E86E1C15' : 'transparent',
                  color: activeTab === tab.key ? '#E86E1C' : '#605e5c',
                }}>
                <span style={{ fontSize: 18 }}>{tab.label.split(' ')[0]}</span>
                <span style={{ textAlign: 'center' }}>{tab.label.split(' ').slice(1).join(' ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Desktop: horizontal tabs */}
      <div style={styles.tabs} className="r-tabs r-admin-tab-row">
        {tabs.map(tab => (
          <button key={tab.key} style={{ ...styles.tab, ...(activeTab === tab.key ? styles.tabActive : {}) }}
            onClick={() => setActiveTab(tab.key)}>{tab.label}</button>
        ))}
      </div>

      {/* ==================== OVERZICHT ==================== */}
      {activeTab === 'overzicht' && (
        <div style={styles.section}>
          <div style={styles.statsGrid} className="r-admin-stats">
            <div style={{ ...styles.statCard, borderLeft: '4px solid #FFB900' }} className="r-admin-stat">
              <div style={{ fontSize: 28, marginBottom: 6 }}>📝</div>
              <div style={styles.statNumber} className="stat-val">{openVerlof}</div>
              <div style={styles.statLabel}>Open verlofaanvragen</div>
            </div>
            <div style={{ ...styles.statCard, borderLeft: '4px solid #E86E1C' }} className="r-admin-stat">
              <div style={{ fontSize: 28, marginBottom: 6 }}>⏰</div>
              <div style={styles.statNumber} className="stat-val">{openOveruren}</div>
              <div style={styles.statLabel}>Open overuren</div>
            </div>
            <div style={{ ...styles.statCard, borderLeft: '4px solid #107C10' }} className="r-admin-stat">
              <div style={{ fontSize: 28, marginBottom: 6 }}>✅</div>
              <div style={styles.statNumber} className="stat-val">{goedgekeurdVerlof}</div>
              <div style={styles.statLabel}>Goedgekeurd verlof</div>
            </div>
            <div style={{ ...styles.statCard, borderLeft: '4px solid #0078D4' }} className="r-admin-stat">
              <div style={{ fontSize: 28, marginBottom: 6 }}>👥</div>
              <div style={styles.statNumber} className="stat-val">{medewerkers.length}</div>
              <div style={styles.statLabel}>Medewerkers</div>
            </div>
          </div>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Verlofsaldi (uren)</h3>
            <div className="r-table-wrap">
            <table style={{ ...styles.table, minWidth: 1100 }} className="r-table">
              <thead><tr>
                <th style={styles.th}>Medewerker</th><th style={styles.th}>Rol</th><th style={styles.th}>Week</th>
                <th style={styles.th}>Verlofuren</th><th style={styles.th}>Meegen.</th><th style={styles.th}>Totaal</th><th style={styles.th}>Opgenomen</th>
                <th style={styles.th}>Resterend</th>
                <th style={{ ...styles.th, borderLeft: '2px solid #FFB900' }}>Open overuren</th>
                <th style={styles.th}>Overuren saldo</th>
                <th style={{ ...styles.th, borderLeft: '2px solid #8764B8' }}>Flex/ADV</th>
                <th style={styles.th}>Flex saldo</th>
                <th style={{ ...styles.th, borderLeft: '2px solid #D13438' }}>🏥 Ziekte</th>
              </tr></thead>
              <tbody>
                {medewerkers.filter(m => m.Rol !== 'flexkracht' && (m.Status || 'actief') === 'actief').map(m => {
                  const totaal = m.BerekendeVerlofUren + m.MeegenomenUren;
                  const resterend = totaal - m.VerlofOpgenomen;
                  const isProRata = m.BerekendeVerlofUren !== m.ContractUren * VERLOF_FACTOR;
                  const badges = [];
                  badges.push({ icon: '👤', text: 'MW', color: '#107C10' });
                  if (m.IsManager) badges.push({ icon: '📋', text: 'Mgr', color: '#0078D4' });
                  if (m.IsAdmin) badges.push({ icon: '👑', text: 'Adm', color: '#8764B8' });
                  if (m.HeeftFlexProfiel) badges.push({ icon: '⚡', text: 'Flex', color: '#E86E1C' });
                  if (m.ShowroomMedewerker) badges.push({ icon: '🏪', text: 'SR', color: '#498205' });

                  // Open overuren (status ingediend)
                  const openOU = overuren.filter(o => o.medewerkerId === m.Id && o.Status === 'ingediend')
                    .reduce((s, o) => s + (o.Uren || 0), 0);

                  // Overuren saldo (goedgekeurd minus gebruikt voor verlof minus uitbetaald)
                  const ouVerdiend = overuren.filter(o => o.medewerkerId === m.Id && o.Status === 'goedgekeurd')
                    .reduce((s, o) => s + (o.Uren || 0), 0);
                  const ouGebruikt = verlofaanvragen.filter(v => v.medewerkerId === m.Id && v.Status === 'goedgekeurd' && v.jaar === currentYear)
                    .reduce((s, v) => s + (v.overurenGebruikt || 0), 0);
                  const ouUitbetaald = overuren.filter(o => o.medewerkerId === m.Id && o.Status === 'uitbetaald')
                    .reduce((s, o) => s + (o.Uren || 0), 0);
                  const ouSaldo = ouVerdiend - ouGebruikt - ouUitbetaald;

                  // Flex/ADV saldo
                  const flexBoek = verlofaanvragen.filter(v => {
                    const vt = verlofTypes.find(t => t.key === v.Type);
                    return v.medewerkerId === m.Id && v.Status === 'goedgekeurd' && v.jaar === currentYear &&
                      (v.Type === 'flex-plus' || vt?.aftrekSaldo === 'flex-plus' || vt?.aftrekSaldo === 'flex');
                  });
                  const flexPlus = flexBoek.filter(v => {
                    const vt = verlofTypes.find(t => t.key === v.Type);
                    return v.Type === 'flex-plus' || vt?.aftrekSaldo === 'flex-plus';
                  }).reduce((s, v) => s + (v.Uren || 0), 0);
                  const flexMin = flexBoek.filter(v => {
                    const vt = verlofTypes.find(t => t.key === v.Type);
                    return vt?.aftrekSaldo === 'flex';
                  }).reduce((s, v) => s + (v.Uren || 0), 0);
                  const flexSaldo = flexPlus - flexMin;

                  // Actuele ziekte-uren (open ziekmeldingen → berekend tot nu)
                  const openZiek = verlofaanvragen.filter(v =>
                    v.medewerkerId === m.Id && v.isOpenZiek && v.Type === 'ziek'
                  );
                  let ziekUren = 0;
                  let ziekSinds = null;
                  if (openZiek.length > 0) {
                    const z = openZiek[0]; // meest recente
                    ziekSinds = z.StartDatum;
                    const nu = new Date();
                    const vandaag = nu.toISOString().split('T')[0];
                    const nuTijd = `${String(nu.getHours()).padStart(2,'0')}:${String(nu.getMinutes()).padStart(2,'0')}`;
                    try {
                      if (z.StartTijd) {
                        // Met start/eindtijd → preciezer
                        const info = berekenVerlofUrenHeleDag(z.StartDatum, vandaag, feestdagen.map(f => f.datum || f.Datum), m.Werkrooster);
                        ziekUren = info.uren || 0;
                      } else {
                        const info = berekenVerlofUrenHeleDag(z.StartDatum, vandaag, feestdagen.map(f => f.datum || f.Datum), m.Werkrooster);
                        ziekUren = info.uren || 0;
                      }
                    } catch { ziekUren = 0; }
                  }
                  // Afgesloten ziekte dit jaar
                  const ziekAf = verlofaanvragen.filter(v =>
                    v.medewerkerId === m.Id && !v.isOpenZiek && v.Type === 'ziek' && v.Status === 'goedgekeurd' && v.jaar === currentYear
                  ).reduce((s, v) => s + (v.Uren || 0), 0);
                  const ziekTotaal = ziekUren + ziekAf;

                  return (
                    <tr key={m.Id} style={styles.tr}>
                      <td style={styles.td}><strong>{m.Naam}</strong>{m.IndienstDatum && <div style={{ fontSize: 10, color: '#a19f9d' }}>in dienst: {fmtDatum(m.IndienstDatum)}</div>}</td>
                      <td style={styles.td}><div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>{badges.map((b, i) => <span key={i} style={{ ...styles.roleBadge, background: `${b.color}20`, color: b.color, fontSize: 10 }}>{b.icon}</span>)}</div></td>
                      <td style={styles.td}>{m.ContractUren}u/w</td>
                      <td style={styles.td}><span style={{ fontWeight: 600, color: isProRata ? '#E86E1C' : '#323130' }}>{m.BerekendeVerlofUren}u</span>{isProRata && <div style={{ fontSize: 10, color: '#E86E1C' }}>pro-rata</div>}</td>
                      <td style={styles.td}>{m.MeegenomenUren > 0 ? <span style={{ background: '#8764B820', color: '#8764B8', padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600 }}>+{m.MeegenomenUren}u</span> : '0u'}</td>
                      <td style={styles.td}>{totaal}u</td>
                      <td style={styles.td}>{m.VerlofOpgenomen}u</td>
                      <td style={styles.td}><strong style={{ color: resterend < 0 ? '#D13438' : '#107C10' }}>{resterend}u</strong></td>
                      {/* Open overuren */}
                      <td style={{ ...styles.td, borderLeft: '2px solid #FFB90040' }}>{openOU > 0
                        ? <span style={{ background: '#FFF4CE', color: '#7A6400', padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600 }}>⏳ {openOU}u</span>
                        : <span style={{ color: '#a19f9d', fontSize: 12 }}>—</span>}</td>
                      {/* Overuren saldo */}
                      <td style={styles.td}>{ouVerdiend > 0 || ouGebruikt > 0
                        ? <strong style={{ color: ouSaldo > 0 ? '#107C10' : ouSaldo < 0 ? '#D13438' : '#323130' }}>{ouSaldo > 0 ? '+' : ''}{ouSaldo}u</strong>
                        : <span style={{ color: '#a19f9d', fontSize: 12 }}>—</span>}</td>
                      {/* Flex/ADV status */}
                      <td style={{ ...styles.td, borderLeft: '2px solid #8764B840' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: m.FlexEnabled ? '#dff6dd' : '#f3f2f1', color: m.FlexEnabled ? '#107C10' : '#a19f9d' }}>
                          {m.FlexEnabled ? `Aan (${m.WeekFlexUren}u/w)` : 'Uit'}
                        </span>
                      </td>
                      {/* Flex saldo */}
                      <td style={styles.td}>{m.FlexEnabled
                        ? <strong style={{ color: flexSaldo > 0 ? '#107C10' : flexSaldo < 0 ? '#D13438' : '#323130' }}>{flexSaldo > 0 ? '+' : ''}{flexSaldo}u</strong>
                        : <span style={{ color: '#a19f9d', fontSize: 12 }}>—</span>}</td>
                      {/* Ziekte */}
                      <td style={{ ...styles.td, borderLeft: '2px solid #D1343840' }}>{openZiek.length > 0
                        ? <div>
                            <span style={{ background: '#D1343820', color: '#D13438', padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 700 }}>
                              🏥 {Math.round(ziekUren * 10) / 10}u
                            </span>
                            <div style={{ fontSize: 10, color: '#D13438', marginTop: 2 }}>
                              sinds {fmtDatum(ziekSinds)}
                            </div>
                            {ziekAf > 0 && <div style={{ fontSize: 10, color: '#a19f9d' }}>+{ziekAf}u eerder</div>}
                          </div>
                        : ziekTotaal > 0
                          ? <span style={{ fontSize: 12, color: '#605e5c' }}>{ziekTotaal}u dit jaar</span>
                          : <span style={{ color: '#a19f9d', fontSize: 12 }}>—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}


      {/* ==================== MEDEWERKERS ==================== */}
      {activeTab === 'medewerkers' && (
        <div style={styles.section}>
          {managerOnly && (
            <div style={{ background: '#fff4ce', border: '1px solid #FFB900', borderRadius: 10, padding: '10px 16px', fontSize: 13 }}>
              <strong style={{ color: '#7A6400' }}>👔 Manager: </strong>
              <span style={{ color: '#605e5c' }}>Je kunt hier tijdelijke flexkrachten aanmaken. Beheerder keurt ze goed zodat ze kunnen inloggen.</span>
            </div>
          )}
          <div style={styles.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }} className="r-admin-header">
              <h3 style={styles.cardTitle}>{managerOnly ? '⚡ Flexkrachten' : `Medewerkers (${medewerkers.length})`}</h3>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {!managerOnly && <button style={{ ...styles.addButton, background: '#0078D4' }} onClick={() => {
                  const y = currentYear;
                  setPdfDialog({ vanDatum: `${y}-01-01`, totDatum: `${y}-12-31`, medewerkerId: 'alle' });
                }}>📄 PDF Totaaloverzicht</button>}
                {!managerOnly && <button style={{ ...styles.addButton, background: '#107C10' }} onClick={() => {
                  exportMedewerkersCSV(medewerkers, verlofaanvragen, overuren, verlofTypes, currentYear);
                }}>📊 CSV Export</button>}
                <button style={styles.addButton} onClick={() => {
                  // Manager: pre-select flexkracht rol
                  startNewMedewerker();
                  if (managerOnly) setNewMedewerker(prev => prev ? { ...prev, isFlexkracht: true, pendingApproval: true } : null);
                }}>+ {managerOnly ? 'Flexkracht toevoegen' : 'Toevoegen'}</button>
              </div>
            </div>

            {newMedewerker && (() => {
              const currentYear = new Date().getFullYear();
              const preview = berekenProRataPreview(
                newMedewerker.contractUren, newMedewerker.indienstDatum, [], currentYear
              );
              const isPartYear = newMedewerker.indienstDatum && new Date(newMedewerker.indienstDatum) > new Date(currentYear, 0, 1);
              return (
              <div style={styles.editRow}>
                <div style={styles.editGrid} className="r-form-grid">
                  <div><label style={styles.editLabel}>Naam *</label><input style={styles.editInput} value={newMedewerker.naam} onChange={e => setNewMedewerker({...newMedewerker, naam: e.target.value})} placeholder="Volledige naam" /></div>
                  <div><label style={styles.editLabel}>Korte naam</label><input style={styles.editInput} value={newMedewerker.verkorteNaam} onChange={e => setNewMedewerker({...newMedewerker, verkorteNaam: e.target.value})} placeholder="Outlook weergave (bijv. Erwin)" /></div>
                  <div><label style={styles.editLabel}>E-mail *</label><input style={styles.editInput} value={newMedewerker.email} onChange={e => setNewMedewerker({...newMedewerker, email: e.target.value})} placeholder="naam@bedrijf.nl" /></div>
                  <div><label style={styles.editLabel}>Wachtwoord *</label><input style={styles.editInput} type="password" value={newMedewerker.wachtwoord} onChange={e => setNewMedewerker({...newMedewerker, wachtwoord: e.target.value})} placeholder="Min. 8, hoofdletter, kleine letter, cijfer" />
                    {newMedewerker.wachtwoord && !valideerWachtwoord(newMedewerker.wachtwoord).valid && (
                      <div style={{ fontSize: 11, color: '#D83B01', marginTop: 2 }}>{valideerWachtwoord(newMedewerker.wachtwoord).errors.join(' · ')}</div>
                    )}
                  </div>
                  <div><label style={styles.editLabel}>Afdeling</label><input style={styles.editInput} value={newMedewerker.afdeling} onChange={e => setNewMedewerker({...newMedewerker, afdeling: e.target.value})} /></div>
                  <div><label style={styles.editLabel}>Telefoon</label><input style={styles.editInput} value={newMedewerker.telefoon} onChange={e => setNewMedewerker({...newMedewerker, telefoon: e.target.value})} placeholder="06-12345678" /></div>
                  <div><label style={styles.editLabel}>Kleur</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 4 }}>
                      <input type="color" value={newMedewerker.kleur || '#0078D4'} onChange={e => setNewMedewerker({...newMedewerker, kleur: e.target.value})} style={{ width: 36, height: 28, border: '1px solid #edebe9', borderRadius: 4, padding: 0, cursor: 'pointer' }} />
                      <span style={{ fontSize: 10, color: '#a19f9d' }}>Kalender & Outlook</span>
                    </div>
                  </div>
                  <div><label style={styles.editLabel}>Profielen</label>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', paddingTop: 4 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}><input type="checkbox" checked={newMedewerker.isFlexkracht} onChange={e => setNewMedewerker({...newMedewerker, isFlexkracht: e.target.checked, isManager: false, isAdmin: false, ...(e.target.checked ? { contractUren: 0, flexEnabled: false, weekFlexUren: 0, meegenomenUren: 0, showroomMedewerker: false } : {})})} />⚡ Flexkracht</label>
                      {!newMedewerker.isFlexkracht && <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}><input type="checkbox" checked={newMedewerker.isManager} onChange={e => setNewMedewerker({...newMedewerker, isManager: e.target.checked})} />📋 Manager</label>}
                      {!newMedewerker.isFlexkracht && <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}><input type="checkbox" checked={newMedewerker.isAdmin} onChange={e => setNewMedewerker({...newMedewerker, isAdmin: e.target.checked})} />👑 Beheerder</label>}
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, borderLeft: '1px solid #edebe9', paddingLeft: 12 }}><input type="checkbox" checked={newMedewerker.heeftFlexProfiel} onChange={e => setNewMedewerker({...newMedewerker, heeftFlexProfiel: e.target.checked})} />⚡ Flexkracht profiel</label>
                      {!newMedewerker.isFlexkracht && <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, borderLeft: '1px solid #edebe9', paddingLeft: 12 }}><input type="checkbox" checked={newMedewerker.showroomMedewerker} onChange={e => setNewMedewerker({...newMedewerker, showroomMedewerker: e.target.checked})} />🏪 Showroom</label>}
                    </div>
                  </div>
                  {!newMedewerker.isFlexkracht && <>
                  <div><label style={styles.editLabel}>Contracturen per week</label><input style={styles.editInput} type="number" value={newMedewerker.contractUren} onChange={e => setNewMedewerker({...newMedewerker, contractUren: Number(e.target.value)})} /></div>
                  <div><label style={styles.editLabel}>Datum in dienst</label><input style={styles.editInput} type="date" value={newMedewerker.indienstDatum} onChange={e => setNewMedewerker({...newMedewerker, indienstDatum: e.target.value})} /></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20 }}>
                    <input type="checkbox" checked={newMedewerker.flexEnabled} onChange={e => setNewMedewerker({...newMedewerker, flexEnabled: e.target.checked, weekFlexUren: e.target.checked ? (newMedewerker.weekFlexUren || 4) : 0})} />
                    <label style={styles.editLabel}>🔀 Flex/ADV</label>
                    {newMedewerker.flexEnabled && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8 }}>
                        <input style={{...styles.editInput, width: 50}} type="number" min="0" max="40" value={newMedewerker.weekFlexUren || 4} onChange={e => setNewMedewerker({...newMedewerker, weekFlexUren: Number(e.target.value)})} />
                        <span style={{ fontSize: 11, color: '#605e5c' }}>u/week</span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 8 }}>
                    <input type="checkbox" checked={(newMedewerker.zaterdagDrempel || 0) > 0} onChange={e => setNewMedewerker({...newMedewerker, zaterdagDrempel: e.target.checked ? 24 : 0})} />
                    <label style={styles.editLabel}>📅 Zaterdag onbetaald</label>
                    {(newMedewerker.zaterdagDrempel || 0) > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8 }}>
                        <input style={{...styles.editInput, width: 50}} type="number" min="0" max="200" value={newMedewerker.zaterdagDrempel} onChange={e => setNewMedewerker({...newMedewerker, zaterdagDrempel: Number(e.target.value)})} />
                        <span style={{ fontSize: 11, color: '#605e5c' }}>uur/jaar</span>
                      </div>
                    )}
                  </div>
                </>}
                {newMedewerker.isFlexkracht && (
                  <div style={{ background: '#fff3e0', border: '1px solid #ff9800', borderRadius: 10, padding: '14px 18px', margin: '12px 0' }}>
                    <div style={{ fontSize: 13, color: '#e65100', fontWeight: 600 }}>⚡ Flexkracht account</div>
                    <div style={{ fontSize: 12, color: '#795548', marginTop: 4 }}>Flexkrachten hebben geen verlofuren, contracturen of Flex/ADV. Alleen flex-inzet planning is beschikbaar.</div>
                  </div>
                )}
                </div>

                {/* Live preview verlofuren */}
                {!newMedewerker.isFlexkracht && (
                <div style={{ background: '#f0f6ff', border: '1px solid #c7e0f4', borderRadius: 10, padding: '14px 18px', margin: '12px 0' }}>
                  <div style={{ fontSize: 14, color: '#0078D4', fontWeight: 600, marginBottom: 4 }}>
                    📊 Verlofuren {currentYear}: <strong>{preview}u</strong>
                    <span style={{ fontSize: 12, fontWeight: 400, color: '#605e5c', marginLeft: 8 }}>
                      ({newMedewerker.contractUren}u/week × 5 = {newMedewerker.contractUren * VERLOF_FACTOR}u/jaar)
                    </span>
                  </div>
                  {isPartYear ? (
                    <div style={{ fontSize: 12, color: '#605e5c' }}>
                      Pro-rata: in dienst per {new Date(newMedewerker.indienstDatum).toLocaleDateString('nl-NL')} → {preview}u van {newMedewerker.contractUren * VERLOF_FACTOR}u op jaarbasis
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#605e5c' }}>
                      Volledig jaar → {newMedewerker.contractUren * VERLOF_FACTOR}u verlofuren
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#a19f9d', marginTop: 6 }}>
                    ℹ️ Meegenomen uren worden niet automatisch ingesteld. Dit doet de beheerder handmatig via de tabel of bij jaarafsluiting.
                  </div>
                </div>
                )}

                <div style={styles.editActions}>
                  <button style={styles.saveBtn} onClick={saveNewMedewerker} disabled={processing}>Opslaan</button>
                  <button style={styles.cancelBtn} onClick={() => setNewMedewerker(null)}>Annuleren</button>
                </div>
              </div>
              );
            })()}

            {(() => {
              const actieve = medewerkers.filter(m => (m.Status || 'actief') === 'actief');
              const inactieve = medewerkers.filter(m => m.Status === 'inactief');
              const uitDienst = medewerkers.filter(m => m.Status === 'uitdienst');

              const rolBadges = (m) => {
                const badges = [];
                if (m.Rol === 'flexkracht') badges.push({ icon: '⚡', text: 'Flex', color: '#E86E1C' });
                else {
                  badges.push({ icon: '👤', text: 'Medewerker', color: '#107C10' });
                  if (m.IsManager) badges.push({ icon: '📋', text: 'Manager', color: '#0078D4' });
                  if (m.IsAdmin) badges.push({ icon: '👑', text: 'Beheerder', color: '#8764B8' });
                  if (m.HeeftFlexProfiel) badges.push({ icon: '⚡', text: 'Flexkracht', color: '#E86E1C' });
                  if (m.FlexEnabled) badges.push({ icon: '🔀', text: `ADV ${m.WeekFlexUren || 0}u/w`, color: '#00B7C3' });
                  if (m.ShowroomMedewerker) badges.push({ icon: '🏪', text: 'Showroom', color: '#498205' });
                }
                return badges.map((b, i) => (
                  <span key={i} style={{ ...styles.roleBadge, background: `${b.color}20`, color: b.color, marginRight: 4, marginBottom: 2 }}>{b.icon} {b.text}</span>
                ));
              };

              const renderEditRow = (m) => (
                <tr key={m.Id} style={{ ...styles.tr, background: '#faf9f8' }}>
                  <td style={styles.td}>
                    <input style={styles.editInput} value={editForm.naam} onChange={e => setEditForm({...editForm, naam: e.target.value})} />
                    <input style={{...styles.editInput, marginTop: 2, fontSize: 10, color: '#605e5c'}} value={editForm.verkorteNaam} onChange={e => setEditForm({...editForm, verkorteNaam: e.target.value})} placeholder="Korte naam (Outlook)" />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                      <label style={{ fontSize: 10, color: '#605e5c', fontWeight: 600 }}>Kleur:</label>
                      <input type="color" value={editForm.kleur || medewerkerKleurMap[m.Id] || '#0078D4'} onChange={e => setEditForm({...editForm, kleur: e.target.value})} style={{ width: 28, height: 22, border: '1px solid #edebe9', borderRadius: 4, padding: 0, cursor: 'pointer' }} />
                      {editForm.kleur && <button onClick={() => setEditForm({...editForm, kleur: ''})} style={{ fontSize: 9, color: '#a19f9d', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>✕ reset</button>}
                    </div>
                  </td>
                  <td style={styles.td}><input style={styles.editInput} value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} /></td>
                  <td style={styles.td}><input style={styles.editInput} value={editForm.telefoon} onChange={e => setEditForm({...editForm, telefoon: e.target.value})} placeholder="06-..." /></td>
                  <td style={styles.td}><input style={styles.editInput} value={editForm.afdeling} onChange={e => setEditForm({...editForm, afdeling: e.target.value})} /></td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}><input type="checkbox" checked={editForm.isFlexkracht} onChange={e => setEditForm({...editForm, isFlexkracht: e.target.checked, isManager: false, isAdmin: false, ...(e.target.checked ? { contractUren: 0, flexEnabled: false, weekFlexUren: 0, meegenomenUren: 0, showroomMedewerker: false } : {})})} />⚡ Flex</label>
                      {!editForm.isFlexkracht && <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}><input type="checkbox" checked={editForm.isManager} onChange={e => setEditForm({...editForm, isManager: e.target.checked})} />📋 Manager</label>}
                      {!editForm.isFlexkracht && (() => {
                        const isLaatsteBeheerder = m.IsAdmin && aantalBeheerders <= 1;
                        return (
                          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, opacity: isLaatsteBeheerder ? 0.5 : 1 }}
                            title={isLaatsteBeheerder ? 'Laatste beheerder — maak eerst iemand anders beheerder' : ''}>
                            <input type="checkbox" checked={editForm.isAdmin}
                              disabled={isLaatsteBeheerder}
                              onChange={e => setEditForm({...editForm, isAdmin: e.target.checked})} />
                            👑 Beheerder {isLaatsteBeheerder && '🔒'}
                          </label>
                        );
                      })()}
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, marginTop: 4, borderTop: '1px solid #edebe9', paddingTop: 4 }}><input type="checkbox" checked={editForm.heeftFlexProfiel} onChange={e => setEditForm({...editForm, heeftFlexProfiel: e.target.checked})} />⚡ Flexkracht profiel</label>
                      {!editForm.isFlexkracht && <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}><input type="checkbox" checked={editForm.showroomMedewerker} onChange={e => setEditForm({...editForm, showroomMedewerker: e.target.checked})} />🏪 Showroom</label>}
                      {!editForm.isFlexkracht && (
                        <div style={{ marginTop: 4, borderTop: '1px solid #edebe9', paddingTop: 4 }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                            <input type="checkbox" checked={(editForm.zaterdagDrempel || 0) > 0} onChange={e => setEditForm({...editForm, zaterdagDrempel: e.target.checked ? (editForm.zaterdagDrempel || 24) : 0})} />
                            📅 Zaterdag onbetaald
                          </label>
                          {(editForm.zaterdagDrempel || 0) > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, paddingLeft: 20 }}>
                              <input style={{...styles.editInput, width: 50, padding: '1px 4px', fontSize: 10}} type="number" min="0" max="200" value={editForm.zaterdagDrempel} onChange={e => setEditForm({...editForm, zaterdagDrempel: Number(e.target.value)})} />
                              <span style={{ fontSize: 9, color: '#605e5c' }}>uur onbetaald/jaar</span>
                            </div>
                          )}
                        </div>
                      )}
                      {!editForm.isFlexkracht && (
                        <div style={{ marginTop: 4, borderTop: '1px solid #edebe9', paddingTop: 4 }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                            <input type="checkbox" checked={editForm.flexEnabled} onChange={e => setEditForm({...editForm, flexEnabled: e.target.checked, weekFlexUren: e.target.checked ? (editForm.weekFlexUren || 4) : 0})} />
                            🔀 Flex/ADV
                          </label>
                          {editForm.flexEnabled && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, paddingLeft: 20 }}>
                              <input style={{...styles.editInput, width: 40, padding: '1px 4px', fontSize: 10}} type="number" min="0" max="40" value={editForm.weekFlexUren} onChange={e => setEditForm({...editForm, weekFlexUren: Number(e.target.value)})} />
                              <span style={{ fontSize: 9, color: '#605e5c' }}>u/week</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={styles.td}>{editForm.isFlexkracht ? <span style={{ fontSize: 10, color: '#a19f9d' }}>n.v.t.</span> : <input style={{...styles.editInput, width: 60}} type="number" value={editForm.contractUren} onChange={e => setEditForm({...editForm, contractUren: e.target.value})} />}</td>
                  <td style={styles.td}><input style={{...styles.editInput, width: 100}} type="date" value={editForm.indienstDatum} onChange={e => setEditForm({...editForm, indienstDatum: e.target.value})} /></td>
                  <td style={styles.td}>{editForm.isFlexkracht ? <span style={{ fontSize: 10, color: '#a19f9d' }}>n.v.t.</span> : <input style={{...styles.editInput, width: 50}} type="number" value={editForm.meegenomenUren} onChange={e => setEditForm({...editForm, meegenomenUren: e.target.value})} />}</td>
                  <td style={styles.td}>
                    <input style={{...styles.editInput, width: 90}} type="text" value={editForm.wachtwoord} onChange={e => setEditForm({...editForm, wachtwoord: e.target.value})} placeholder="(niet wijzigen)" title="Laat leeg om niet te wijzigen" />
                  </td>
                  <td style={styles.td}><div style={styles.actionRow}><button style={styles.saveBtn} onClick={saveEdit} disabled={processing}>✓</button><button style={styles.cancelBtn} onClick={() => setEditingId(null)}>✕</button></div></td>
                </tr>
              );

              const renderDisplayRow = (m, acties) => (
                <tr key={m.Id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 14, height: 14, borderRadius: '50%', background: medewerkerKleurMap[m.Id] || '#0078D4', flexShrink: 0, border: '2px solid #fff', boxShadow: '0 0 0 1px #edebe9' }} />
                      <div><strong>{m.Naam}</strong>{m.VerkorteNaam && <div style={{ fontSize: 10, color: '#0078D4' }}>📅 {m.VerkorteNaam}</div>}</div>
                    </div>
                  </td>
                  <td style={styles.td}><span style={{ fontSize: 12 }}>{m.Email}</span></td>
                  <td style={styles.td}><span style={{ fontSize: 12 }}>{m.Telefoon || '—'}</span></td>
                  <td style={styles.td}>{m.Afdeling}</td>
                  <td style={styles.td}><div style={{ display: 'flex', flexWrap: 'wrap' }}>{rolBadges(m)}</div></td>
                  <td style={styles.td}>{m.ContractUren}u/w</td>
                  <td style={styles.td}><span style={{ fontSize: 12 }}>{fmtDatum(m.IndienstDatum)}</span></td>
                  <td style={styles.td}>{m.MeegenomenUren > 0 ? <span style={{ background: '#8764B820', color: '#8764B8', padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600 }}>+{m.MeegenomenUren}u</span> : '0u'}</td>
                  <td style={styles.td}><strong>{m.BerekendeVerlofUren}u</strong></td>
                  <td style={styles.td}><div style={styles.actionRow}>{acties}</div></td>
                </tr>
              );

              const tableHeader = (
                <thead><tr>
                  <th style={styles.th}>Naam</th><th style={styles.th}>E-mail</th><th style={styles.th}>Telefoon</th><th style={styles.th}>Afdeling</th>
                  <th style={styles.th}>Profielen</th><th style={styles.th}>Week</th><th style={styles.th}>In dienst</th><th style={styles.th}>Meegen.</th>
                  <th style={styles.th}>Verlof</th><th style={styles.th}>Acties</th>
                </tr></thead>
              );

              const editTableHeader = (
                <thead><tr>
                  <th style={styles.th}>Naam</th><th style={styles.th}>E-mail</th><th style={styles.th}>Telefoon</th><th style={styles.th}>Afdeling</th>
                  <th style={styles.th}>Profielen</th><th style={styles.th}>Week</th><th style={styles.th}>In dienst</th><th style={styles.th}>Meegen.</th>
                  <th style={styles.th}>Wachtwoord</th><th style={styles.th}>Acties</th>
                </tr></thead>
              );

              // stuurWhatsAppUitnodiging is defined at component level above
              return (
                <>
                  {/* ACTIEVE MEDEWERKERS */}
                  <h4 style={{ fontSize: 14, color: '#107C10', marginBottom: 8, marginTop: 8 }}>✅ Actieve medewerkers ({actieve.length})</h4>
                  <div className="r-table-wrap">
                  <table style={styles.table} className="r-table">
                    {actieve.some(m => editingId === m.Id) ? editTableHeader : tableHeader}
                    <tbody>
                      {actieve.map(m => editingId === m.Id ? renderEditRow(m) : renderDisplayRow(m, (
                        <>
                          <button style={styles.editBtn} onClick={() => startEdit(m)}>✏️</button>
                          <button style={{ ...styles.editBtn, background: '#25D36620', border: '1px solid #25D366', fontSize: 10 }}
                            onClick={() => stuurWhatsAppUitnodiging(m)} title="WhatsApp uitnodiging versturen">📲</button>
                          <button style={{ ...styles.editBtn, background: '#0078D420', border: '1px solid #0078D4', fontSize: 10 }}
                            onClick={() => {
                              try {
                                const doc = genereerMedewerkerPDF(m, verlofaanvragen, overuren, verlofTypes, currentYear, { roosterWijzigingen, flexPlanning, flexUren, flexUitbetalingen });
                                const blob = doc.output('blob');
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `verlof-${m.Naam.replace(/\s+/g, '-').toLowerCase()}-${currentYear}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                              } catch (err) {
                                console.error('PDF fout:', err);
                                alert('PDF genereren mislukt: ' + err.message);
                              }
                            }} title="PDF overzicht">📄</button>
                          <button style={{ ...styles.editBtn, background: '#8764B820', border: '1px solid #8764B8', fontSize: 10 }}
                            onClick={() => setWwResetForm({ mId: m.Id, naam: m.Naam, nieuw: '', bevestig: '' })}
                            title="Wachtwoord resetten">🔑</button>
                          <button style={{ ...styles.editBtn, background: '#FF880020', border: '1px solid #FF8800', fontSize: 10 }}
                            onClick={() => setResetConfirm({ mId: m.Id, naam: m.Naam })}
                            title="Alle mutaties resetten (testperiode afsluiten)">🔄</button>
                          <button style={{ ...styles.editBtn, background: '#FFB90020', border: '1px solid #FFB900', fontSize: 10 }} onClick={() => wijzigStatus(m.Id, 'inactief')} title="Inactief zetten">⏸️</button>
                          <button style={{ ...styles.editBtn, background: '#D1343820', border: '1px solid #D13438', fontSize: 10 }} onClick={() => wijzigStatus(m.Id, 'uitdienst')} title="Uit dienst melden">🚫</button>
                        </>
                      )))}
                    </tbody>
                  </table>
                  </div>

                  {/* INACTIEVE MEDEWERKERS */}
                  {inactieve.length > 0 && (
                    <>
                      <h4 style={{ fontSize: 14, color: '#FFB900', marginBottom: 8, marginTop: 24 }}>⏸️ Inactieve medewerkers ({inactieve.length})</h4>
                      <p style={{ fontSize: 12, color: '#605e5c', marginBottom: 8 }}>Tijdelijk geen toegang tot het portaal. Gegevens blijven behouden.</p>
                      <div className="r-table-wrap">
                      <table style={styles.table} className="r-table">
                        {inactieve.some(m => editingId === m.Id) ? editTableHeader : tableHeader}
                        <tbody>
                          {inactieve.map(m => editingId === m.Id ? renderEditRow(m) : renderDisplayRow(m, (
                            <>
                              <button style={styles.editBtn} onClick={() => startEdit(m)} title="Bewerken">✏️</button>
                              <button style={{ ...styles.editBtn, background: '#107C1020', border: '1px solid #107C10' }} onClick={() => wijzigStatus(m.Id, 'actief')} title="Activeren">✅</button>
                              <button style={{ ...styles.editBtn, background: '#D1343820', border: '1px solid #D13438', fontSize: 10 }} onClick={() => wijzigStatus(m.Id, 'uitdienst')} title="Uit dienst">🚫</button>
                            </>
                          )))}
                        </tbody>
                      </table>
                      </div>
                    </>
                  )}

                  {/* UIT DIENST */}
                  {uitDienst.length > 0 && (
                    <>
                      <h4 style={{ fontSize: 14, color: '#D13438', marginBottom: 8, marginTop: 24 }}>🚫 Medewerkers uit dienst ({uitDienst.length})</h4>
                      <p style={{ fontSize: 12, color: '#605e5c', marginBottom: 8 }}>Geen toegang. Verlof- en agenda-items blijven zichtbaar. Terug in dienst zetten met nieuwe begindatum.</p>
                      <div className="r-table-wrap">
                      <table style={styles.table} className="r-table">
                        <thead><tr>
                          <th style={styles.th}>Naam</th><th style={styles.th}>E-mail</th><th style={styles.th}>Afdeling</th>
                          <th style={styles.th}>Uit dienst</th><th style={styles.th}>Acties</th>
                        </tr></thead>
                        <tbody>
                          {uitDienst.map(m => (
                            <React.Fragment key={m.Id}>
                              <tr style={styles.tr}>
                                <td style={styles.td}><strong>{m.Naam}</strong></td>
                                <td style={styles.td}><span style={{ fontSize: 12 }}>{m.Email}</span></td>
                                <td style={styles.td}>{m.Afdeling}</td>
                                <td style={styles.td}><span style={{ fontSize: 12 }}>{fmtDatum(m.UitDienstDatum)}</span></td>
                                <td style={styles.td}>
                                  <div style={styles.actionRow}>
                                    <button style={{ ...styles.editBtn, background: '#107C1020', border: '1px solid #107C10', fontSize: 11, padding: '4px 10px' }}
                                      onClick={() => setTerugInDienstForm({ mId: m.Id, datum: new Date().toISOString().split('T')[0] })}
                                      title="Terug in dienst">↩️ Terug in dienst</button>
                                    <button style={{ ...styles.editBtn, background: '#D1343820', border: '1px solid #D13438', fontSize: 11, padding: '4px 10px' }}
                                      onClick={() => {
                                        if (m.IsAdmin && (m.Status || 'actief') === 'actief' && aantalBeheerders <= 1) {
                                          alert('Laatste beheerder kan niet worden verwijderd.');
                                          return;
                                        }
                                        setDeleteConfirm({ mId: m.Id, naam: m.Naam, stap: 1 });
                                      }}
                                      title="Definitief verwijderen">🗑️ Verwijderen</button>
                                  </div>
                                </td>
                              </tr>
                              {terugInDienstForm && terugInDienstForm.mId === m.Id && (
                                <tr style={{ background: '#f0fff0' }}>
                                  <td colSpan={5} style={{ padding: '12px 16px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                      <span style={{ fontSize: 13, fontWeight: 600, color: '#107C10' }}>↩️ {m.Naam} terug in dienst</span>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <label style={{ fontSize: 12, fontWeight: 600 }}>Nieuwe startdatum:</label>
                                        <input type="date" style={{ ...styles.editInput, width: 140 }} value={terugInDienstForm.datum}
                                          onChange={e => setTerugInDienstForm({ ...terugInDienstForm, datum: e.target.value })} />
                                      </div>
                                      <button style={styles.saveBtn} onClick={async () => {
                                        if (terugInDienstForm.datum) {
                                          await wijzigStatus(m.Id, 'actief', { indienstDatum: terugInDienstForm.datum, uitDienstDatum: null });
                                          setTerugInDienstForm(null);
                                        }
                                      }} disabled={processing}>✓ Bevestigen</button>
                                      <button style={styles.cancelBtn} onClick={() => setTerugInDienstForm(null)}>✕</button>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </>
                  )}
                </>
              );
            })()}
          </div>

          {/* DELETE CONFIRMATION MODAL */}
          {deleteConfirm && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
              onClick={() => setDeleteConfirm(null)}>
              <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 440, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
                onClick={e => e.stopPropagation()}>
                {deleteConfirm.stap === 1 ? (
                  <>
                    <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 12 }}>⚠️</div>
                    <h3 style={{ fontSize: 18, fontWeight: 700, color: '#D13438', textAlign: 'center', marginBottom: 12 }}>
                      Medewerker definitief verwijderen?
                    </h3>
                    <p style={{ fontSize: 13, color: '#605e5c', textAlign: 'center', lineHeight: 1.6, marginBottom: 20 }}>
                      Je staat op het punt om <strong>{deleteConfirm.naam}</strong> en alle bijbehorende gegevens te verwijderen:
                    </p>
                    <div style={{ background: '#fde7e9', border: '1px solid #f5c6cb', borderRadius: 10, padding: 14, marginBottom: 20 }}>
                      <div style={{ fontSize: 12, color: '#D13438', lineHeight: 1.8 }}>
                        • Alle verlofaanvragen<br/>
                        • Alle overuren registraties<br/>
                        • Alle roosterwijzigingen<br/>
                        • Alle flex gegevens<br/>
                        • Jaararchief records<br/>
                        • Het medewerker-account zelf
                      </div>
                    </div>
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#D13438', textAlign: 'center', marginBottom: 20 }}>
                      Dit kan NIET ongedaan gemaakt worden!
                    </p>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                      <button style={{ ...styles.cancelBtn, padding: '10px 24px' }} onClick={() => setDeleteConfirm(null)}>Annuleren</button>
                      <button style={{ ...styles.saveBtn, background: '#D13438', padding: '10px 24px' }}
                        onClick={() => setDeleteConfirm({ ...deleteConfirm, stap: 2, bevestigNaam: '' })}>
                        Ik wil verwijderen
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 12 }}>🚨</div>
                    <h3 style={{ fontSize: 18, fontWeight: 700, color: '#D13438', textAlign: 'center', marginBottom: 12 }}>
                      Laatste bevestiging
                    </h3>
                    <p style={{ fontSize: 13, color: '#605e5c', textAlign: 'center', lineHeight: 1.6, marginBottom: 16 }}>
                      Typ de naam van de medewerker om te bevestigen:
                    </p>
                    <div style={{ textAlign: 'center', marginBottom: 8 }}>
                      <strong style={{ fontSize: 16, color: '#D13438' }}>{deleteConfirm.naam}</strong>
                    </div>
                    <input style={{ ...styles.editInput, textAlign: 'center', fontSize: 15, padding: '10px 14px', marginBottom: 20, border: '2px solid #D13438' }}
                      value={deleteConfirm.bevestigNaam || ''}
                      onChange={e => setDeleteConfirm({ ...deleteConfirm, bevestigNaam: e.target.value })}
                      placeholder="Typ naam ter bevestiging..."
                      autoFocus />
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                      <button style={{ ...styles.cancelBtn, padding: '10px 24px' }} onClick={() => setDeleteConfirm(null)}>Annuleren</button>
                      <button style={{ ...styles.saveBtn, background: '#D13438', padding: '10px 24px',
                        opacity: deleteConfirm.bevestigNaam === deleteConfirm.naam ? 1 : 0.4 }}
                        disabled={deleteConfirm.bevestigNaam !== deleteConfirm.naam || processing}
                        onClick={async () => {
                          setProcessing(true);
                          try {
                            await verwijderMedewerkerCompleet(deleteConfirm.mId);
                            setDeleteConfirm(null);
                          } catch (err) {
                            alert('Verwijderen mislukt: ' + err.message);
                          } finally { setProcessing(false); }
                        }}>
                        {processing ? '⏳ Bezig...' : '🗑️ Definitief verwijderen'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* RESET MUTATIES CONFIRMATION MODAL */}
          {resetConfirm && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
              onClick={() => !processing && setResetConfirm(null)}>
              <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 480, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
                onClick={e => e.stopPropagation()}>
                {resetConfirm.resultaat ? (
                  <>
                    <h3 style={{ fontSize: 16, fontWeight: 700, color: '#107C10', marginBottom: 16 }}>✅ Reset voltooid voor {resetConfirm.naam}</h3>
                    <div style={{ background: '#f0fff0', borderRadius: 8, padding: 16, fontSize: 13 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                        <span>📋 Verlofaanvragen:</span><strong>{resetConfirm.resultaat.verlof} verwijderd</strong>
                        <span>⏰ Overuren:</span><strong>{resetConfirm.resultaat.overuren} verwijderd</strong>
                        <span>📂 Jaararchief:</span><strong>{resetConfirm.resultaat.archief} verwijderd</strong>
                        <span>⚡ Flex items:</span><strong>{resetConfirm.resultaat.flex} verwijderd</strong>
                        <span>🔔 Notificaties:</span><strong>{resetConfirm.resultaat.notificaties} verwijderd</strong>
                      </div>
                    </div>
                    <p style={{ fontSize: 12, color: '#605e5c', marginTop: 12 }}>Basisgegevens (naam, e-mail, werkrooster, contract) zijn behouden. Je kunt nu de juiste verloven, overuren en flex opnieuw inrichten.</p>
                    <button style={{ ...styles.saveBtn, marginTop: 16, width: '100%', padding: '10px 24px' }}
                      onClick={() => setResetConfirm(null)}>OK, sluiten</button>
                  </>
                ) : (
                  <>
                    <h3 style={{ fontSize: 16, fontWeight: 700, color: '#FF8800', marginBottom: 8 }}>🔄 Mutaties resetten</h3>
                    <p style={{ fontSize: 13, color: '#605e5c', marginBottom: 16 }}>
                      Je staat op het punt om <strong>alle mutaties</strong> van <strong style={{ color: '#323130' }}>{resetConfirm.naam}</strong> te verwijderen:
                    </p>
                    <div style={{ background: '#FFF4E5', borderRadius: 8, padding: 12, fontSize: 13, marginBottom: 16 }}>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Wordt verwijderd:</div>
                      ✕ Alle verlofaanvragen (vakantie, bijzonder, etc.)<br/>
                      ✕ Alle ziekmeldingen<br/>
                      ✕ Alle overurenregistraties<br/>
                      ✕ Alle flex/ADV items<br/>
                      ✕ Jaararchief<br/>
                      ✕ Notificaties<br/>
                      ✕ Outlook agenda events
                    </div>
                    <div style={{ background: '#f0fff0', borderRadius: 8, padding: 12, fontSize: 13, marginBottom: 16 }}>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Blijft behouden:</div>
                      ✓ Naam, e-mail, telefoon<br/>
                      ✓ Werkrooster & roosterwijzigingen<br/>
                      ✓ Contracturen & indienst datum<br/>
                      ✓ Profiel & rollen
                    </div>
                    <p style={{ fontSize: 12, color: '#D83B01', fontWeight: 600, marginBottom: 16 }}>
                      ⚠️ Dit is bedoeld om een testperiode af te sluiten. Deze actie kan niet ongedaan worden gemaakt.
                    </p>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button style={{ ...styles.cancelBtn, flex: 1, padding: '10px 24px' }}
                        onClick={() => setResetConfirm(null)} disabled={processing}>Annuleren</button>
                      <button style={{ background: '#FF8800', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 24px', fontWeight: 700, cursor: 'pointer', flex: 1, fontSize: 13 }}
                        disabled={processing}
                        onClick={async () => {
                          setProcessing(true);
                          try {
                            // Reset meegenomenUren op medewerker
                            await werkMedewerkerBij(resetConfirm.mId, { meegenomenUren: 0 });
                            const stats = await resetMedewerkerData(resetConfirm.mId);
                            setResetConfirm({ ...resetConfirm, resultaat: stats });
                          } catch (err) {
                            alert('Reset mislukt: ' + err.message);
                          }
                          setProcessing(false);
                        }}>
                        {processing ? '⏳ Bezig...' : '🔄 Reset alle mutaties'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ADMIN WACHTWOORD RESET MODAL */}
          {wwResetForm && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
              onClick={() => !processing && setWwResetForm(null)}>
              <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 420, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
                onClick={e => e.stopPropagation()}>
                <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: '#1a1a2e' }}>🔑 Wachtwoord instellen</h3>

                {/* Succes-state: toon WhatsApp-knop */}
                {wwResetForm.gedaan ? (
                  <div>
                    <div style={{ background: '#dff6dd', color: '#107C10', padding: '12px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
                      ✅ Wachtwoord van <strong>{wwResetForm.naam}</strong> is ingesteld.<br/>
                      Bij de volgende login wordt de medewerker verplicht een nieuw wachtwoord te kiezen.
                    </div>
                    <p style={{ fontSize: 13, color: '#605e5c', marginBottom: 16 }}>
                      Stuur de inloggegevens direct via WhatsApp:
                    </p>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => {
                          const mw = medewerkers.find(m => m.Id === wwResetForm.mId);
                          if (mw) stuurWhatsAppUitnodiging(mw, wwResetForm.nieuw);
                        }}
                        style={{ flex: 2, padding: '11px', border: 'none', borderRadius: 8, background: '#25D366', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        📲 WhatsApp met wachtwoord
                      </button>
                      <button onClick={() => setWwResetForm(null)}
                        style={{ flex: 1, padding: '11px', border: '1px solid #edebe9', borderRadius: 8, background: '#faf9f8', color: '#323130', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        Sluiten
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p style={{ fontSize: 13, color: '#605e5c', marginBottom: 16 }}>
                      Stel een nieuw wachtwoord in voor <strong>{wwResetForm.naam}</strong>. Bij de volgende login wordt de medewerker verplicht zelf een nieuw wachtwoord te kiezen.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#605e5c', display: 'block', marginBottom: 4 }}>Tijdelijk wachtwoord</label>
                        <input type="text" value={wwResetForm.nieuw} onChange={e => setWwResetForm({ ...wwResetForm, nieuw: e.target.value })}
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid #edebe9', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'monospace' }}
                          placeholder="Min. 8 tekens, hoofdletter, kleine letter, cijfer" autoComplete="off" />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#605e5c', display: 'block', marginBottom: 4 }}>Bevestig wachtwoord</label>
                        <input type="text" value={wwResetForm.bevestig} onChange={e => setWwResetForm({ ...wwResetForm, bevestig: e.target.value })}
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid #edebe9', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'monospace' }}
                          placeholder="Herhaal wachtwoord" autoComplete="off" />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                      <button onClick={() => setWwResetForm(null)} disabled={processing}
                        style={{ flex: 1, padding: '10px', border: '1px solid #edebe9', borderRadius: 8, background: '#faf9f8', color: '#323130', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        Annuleren
                      </button>
                      <button disabled={processing || !valideerWachtwoord(wwResetForm.nieuw).valid || wwResetForm.nieuw !== wwResetForm.bevestig}
                        onClick={async () => {
                          setProcessing(true);
                          try {
                            // Admin reset: sla op + zet mustChangePassword vlag
                            await wijzigWachtwoord(wwResetForm.mId, null, wwResetForm.nieuw);
                            await werkMedewerkerBij(wwResetForm.mId, { mustChangePassword: true });
                            setWwResetForm({ ...wwResetForm, gedaan: true });
                          } catch (err) { alert('❌ Mislukt: ' + err.message); }
                          setProcessing(false);
                        }}
                        style={{ flex: 1, padding: '10px', border: 'none', borderRadius: 8, background: '#8764B8', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                          opacity: (!valideerWachtwoord(wwResetForm.nieuw).valid || wwResetForm.nieuw !== wwResetForm.bevestig) ? 0.4 : 1 }}>
                        {processing ? '⏳ Bezig...' : '🔑 Instellen'}
                      </button>
                    </div>
                    {wwResetForm.nieuw && !valideerWachtwoord(wwResetForm.nieuw).valid && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#D83B01' }}>
                        {valideerWachtwoord(wwResetForm.nieuw).errors.map((e, i) => <div key={i}>• {e}</div>)}
                      </div>
                    )}
                    {wwResetForm.nieuw && wwResetForm.bevestig && wwResetForm.nieuw !== wwResetForm.bevestig && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#D83B01' }}>Wachtwoorden komen niet overeen</div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Contract wijziging */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>📋 Contractwijziging doorvoeren</h3>
            <p style={styles.hint}>
              Voer hier een contractwijziging door met ingangsdatum. Het systeem herberekent de verlofuren pro-rata: oude contracturen gelden tot de ingangsdatum, nieuwe uren daarna.
            </p>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }} className="r-form-row">
              <div style={{ flex: 1, minWidth: 180 }}>
                <label style={styles.editLabel}>Medewerker</label>
                <select style={styles.editInput} value={contractWijzForm.medewerkerId} onChange={e => setContractWijzForm({...contractWijzForm, medewerkerId: e.target.value})}>
                  <option value="" disabled>--- Kies ---</option>
                  {medewerkers.filter(m => m.Rol !== 'flexkracht').map(m => (
                    <option key={m.Id} value={m.Id}>{m.Naam} (huidig: {m.ContractUren}u/week)</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <label style={styles.editLabel}>Ingangsdatum</label>
                <input type="date" style={styles.editInput} value={contractWijzForm.ingangsDatum} onChange={e => setContractWijzForm({...contractWijzForm, ingangsDatum: e.target.value})} />
              </div>
              <div style={{ flex: 1, minWidth: 120 }}>
                <label style={styles.editLabel}>Nieuwe weekuren</label>
                <input type="number" style={styles.editInput} value={contractWijzForm.contractUren} onChange={e => setContractWijzForm({...contractWijzForm, contractUren: e.target.value})} placeholder="bijv. 32" />
              </div>
            </div>

            {/* Preview impact */}
            {contractWijzForm.medewerkerId && contractWijzForm.ingangsDatum && contractWijzForm.contractUren && (() => {
              const mw = medewerkers.find(m => m.Id === contractWijzForm.medewerkerId);
              if (!mw) return null;
              const currentYear = new Date().getFullYear();
              const huidigeBerekend = berekenProRataPreview(mw.ContractUren, mw.IndienstDatum, mw.ContractWijzigingen || [], currentYear);
              const nieuweWijzigingen = [...(mw.ContractWijzigingen || []), { ingangsDatum: contractWijzForm.ingangsDatum, contractUren: Number(contractWijzForm.contractUren) }];
              const nieuwBerekend = berekenProRataPreview(mw.ContractUren, mw.IndienstDatum, nieuweWijzigingen, currentYear);
              const verschil = nieuwBerekend - huidigeBerekend;
              return (
                <div style={{ background: '#FFF4EE', border: '1px solid #E86E1C40', borderRadius: 10, padding: '14px 18px', marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e', marginBottom: 6 }}>
                    Impact voor {mw.Naam} in {currentYear}:
                  </div>
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    <div><span style={{ fontSize: 12, color: '#605e5c' }}>Huidig:</span> <strong>{huidigeBerekend}u</strong></div>
                    <div style={{ fontSize: 18, color: '#E86E1C' }}>→</div>
                    <div><span style={{ fontSize: 12, color: '#605e5c' }}>Na wijziging:</span> <strong style={{ color: '#E86E1C' }}>{nieuwBerekend}u</strong></div>
                    <div><span style={{ fontSize: 12, color: '#605e5c' }}>Verschil:</span> <strong style={{ color: verschil >= 0 ? '#107C10' : '#a4262c' }}>{verschil >= 0 ? '+' : ''}{verschil}u</strong></div>
                  </div>
                  <div style={{ fontSize: 11, color: '#a19f9d', marginTop: 6 }}>
                    {mw.ContractUren}u/week ({mw.ContractUren * VERLOF_FACTOR}u verlof/jaar) tot {new Date(contractWijzForm.ingangsDatum).toLocaleDateString('nl-NL')}, daarna {contractWijzForm.contractUren}u/week ({contractWijzForm.contractUren * VERLOF_FACTOR}u verlof/jaar)
                  </div>
                </div>
              );
            })()}

            <button style={styles.addButton} onClick={handleContractWijziging} disabled={processing}>
              {processing ? 'Bezig...' : 'Wijziging doorvoeren'}
            </button>
          </div>

          {/* Contract historie */}
          {medewerkers.filter(m => m.Rol !== 'flexkracht' && (m.Status || 'actief') === 'actief' && m.ContractWijzigingen?.length > 0).length > 0 && (
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Contractwijzigingen historie</h3>
              <div className="r-table-wrap">
              <table style={styles.table} className="r-table">
                <thead><tr>
                  <th style={styles.th}>Medewerker</th><th style={styles.th}>Ingangsdatum</th><th style={styles.th}>Van</th><th style={styles.th}>Naar</th><th style={styles.th}>Vastgelegd op</th><th style={styles.th}>Acties</th>
                </tr></thead>
                <tbody>
                  {medewerkers.filter(m => m.Rol !== 'flexkracht' && (m.Status || 'actief') === 'actief' && m.ContractWijzigingen?.length > 0)
                    .flatMap(m => m.ContractWijzigingen.map((w, i) => ({ ...w, mwId: m.Id, mwNaam: m.Naam, wijzIndex: i, key: `${m.Id}-${i}` })))
                    .sort((a, b) => (b.ingangsDatum || '').localeCompare(a.ingangsDatum || ''))
                    .map(w => (
                      <tr key={w.key} style={styles.tr}>
                        <td style={styles.td}><strong>{w.mwNaam}</strong></td>
                        <td style={styles.td}>{fmtDatum(w.ingangsDatum)}</td>
                        <td style={styles.td}>{w.vorigeUren}u</td>
                        <td style={styles.td}><strong style={{ color: '#E86E1C' }}>{w.contractUren}u</strong></td>
                        <td style={styles.td}><span style={{ fontSize: 11, color: '#a19f9d' }}>{fmtDatum(w.datum)}</span></td>
                        <td style={styles.td}>
                          <button style={styles.deleteBtn} onClick={async () => {
                            if (!window.confirm(`Contractwijziging van ${w.mwNaam} verwijderen?\nHet contract wordt teruggezet naar de vorige waarde.`)) return;
                            const mw = medewerkers.find(m => m.Id === w.mwId);
                            if (!mw) return;
                            const nieuweWijzigingen = (mw.ContractWijzigingen || []).filter((_, i) => i !== w.wijzIndex);
                            // Determine contract uren: most recent remaining or the original (vorigeUren of earliest)
                            let nieuweContractUren;
                            if (nieuweWijzigingen.length === 0) {
                              // Revert to original: vorigeUren from earliest wijziging
                              const earliest = [...(mw.ContractWijzigingen || [])].sort((a, b) => (a.ingangsDatum || '').localeCompare(b.ingangsDatum || ''))[0];
                              nieuweContractUren = earliest?.vorigeUren || mw.ContractUren;
                            } else {
                              // Latest remaining wijziging
                              const latest = [...nieuweWijzigingen].sort((a, b) => (b.ingangsDatum || '').localeCompare(a.ingangsDatum || ''))[0];
                              nieuweContractUren = latest.contractUren;
                            }
                            await werkMedewerkerBij(mw.Id, { contractWijzigingen: nieuweWijzigingen, contractUren: nieuweContractUren });
                          }}>🗑️</button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* Meegenomen uren instellen */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>📦 Meegenomen uren instellen</h3>
            <p style={styles.hint}>
              Meegenomen uren worden <strong>niet automatisch</strong> ingesteld bij een nieuw jaar. De beheerder bepaalt zelf hoeveel uren elke medewerker meeneemt (bijv. restant vorig jaar).
            </p>
            <div className="r-table-wrap">
            <table style={styles.table} className="r-table">
              <thead><tr>
                <th style={styles.th}>Medewerker</th>
                <th style={styles.th}>Weekuren</th>
                <th style={styles.th}>Verlofuren</th>
                <th style={styles.th}>Meegenomen uren</th>
                <th style={styles.th}>Totaal beschikbaar</th>
                <th style={styles.th}>Actie</th>
              </tr></thead>
              <tbody>
                {medewerkers.filter(m => m.Rol !== 'flexkracht' && (m.Status || 'actief') === 'actief').map(m => {
                  const currentYear = new Date().getFullYear();
                  const berekend = m.BerekendeVerlofUren || berekenProRataPreview(m.ContractUren, m.IndienstDatum, m.ContractWijzigingen || [], currentYear);
                  const meegen = m.MeegenomenUren || 0;
                  return (
                    <tr key={m.Id} style={styles.tr}>
                      <td style={styles.td}><strong>{m.Naam}</strong></td>
                      <td style={styles.td}>{m.ContractUren}u/w</td>
                      <td style={styles.td}>{berekend}u</td>
                      <td style={styles.td}>
                        <input
                          type="number" step="0.5"
                          style={{ ...styles.editInput, width: 80, textAlign: 'center', fontWeight: 600, color: '#8764B8' }}
                          defaultValue={meegen}
                          onBlur={async (e) => {
                            const val = parseFloat(e.target.value) || 0;
                            if (val !== meegen) {
                              await werkMedewerkerBij(m.Id, { meegenomenUren: val });
                            }
                          }}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        />
                      </td>
                      <td style={styles.td}>
                        <strong style={{ color: '#107C10', fontSize: 15 }}>{berekend + meegen}u</strong>
                      </td>
                      <td style={styles.td}>
                        {meegen > 0 && (
                          <button style={{ ...styles.cancelBtn, fontSize: 11, padding: '2px 8px' }}
                            onClick={async () => { if (window.confirm(`Meegenomen uren van ${m.Naam} op 0 zetten?`)) await werkMedewerkerBij(m.Id, { meegenomenUren: 0 }); }}>
                            Reset
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}
      {/* ==================== VERLOFTYPEN BEHEER ==================== */}
      {activeTab === 'verloftypen' && (
        <div style={styles.section}>
          <div style={styles.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={styles.cardTitle}>🏷️ Verloftypen beheren</h3>
              <button style={styles.addButton} onClick={() => setNewVerlofType({ key: '', text: '', label: '', color: '#767676', trektAfVanVerlof: true, aftrekSaldo: 'verlof', sortOrder: verlofTypes.length })}>+ Nieuw type</button>
            </div>
            <p style={styles.hint}>
              Beheer de beschikbare verloftypen. Per type instelbaar: naam, agendalabel, kleur, en van welk saldo het aftrekt. Opties: <strong>Verlof saldo</strong> (standaard), <strong>Flex uren (min)</strong> (trekt af van flexsaldo), <strong>Flex uren (plus)</strong> (voegt toe aan flexsaldo), of <strong>Geen aftrek</strong> (bijv. ziek).
            </p>

            {newVerlofType && (
              <div style={{ background: '#faf9f8', border: '1px solid #edebe9', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Nieuw verloftype</h4>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }} className="r-form-row">
                  <div style={{ flex: 1, minWidth: 100 }}>
                    <label style={styles.editLabel}>Sleutel (uniek)</label>
                    <input style={styles.editInput} value={newVerlofType.key} onChange={e => setNewVerlofType({...newVerlofType, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')})} placeholder="bijv. compensatie" />
                  </div>
                  <div style={{ flex: 2, minWidth: 140 }}>
                    <label style={styles.editLabel}>Naam</label>
                    <input style={styles.editInput} value={newVerlofType.text} onChange={e => setNewVerlofType({...newVerlofType, text: e.target.value})} placeholder="Bijv. Compensatieverlof" />
                  </div>
                  <div>
                    <label style={styles.editLabel}>Agendalabel</label>
                    <input style={{...styles.editInput, maxWidth: 80}} value={newVerlofType.label} onChange={e => setNewVerlofType({...newVerlofType, label: e.target.value.toUpperCase().slice(0, 4)})} placeholder="CV" maxLength={4} />
                  </div>
                  <div>
                    <label style={styles.editLabel}>Kleur</label>
                    <input type="color" style={{ ...styles.editInput, padding: 2, height: 36, cursor: 'pointer', maxWidth: 70 }} value={newVerlofType.color} onChange={e => setNewVerlofType({...newVerlofType, color: e.target.value})} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 4 }}>
                    <label style={{ fontSize: 12, color: '#323130' }}>Aftrek van:</label>
                    <select style={{ ...styles.editInput, maxWidth: 130 }} value={newVerlofType.aftrekSaldo || 'verlof'}
                      onChange={e => setNewVerlofType({...newVerlofType, aftrekSaldo: e.target.value, trektAfVanVerlof: e.target.value === 'verlof'})}>
                      <option value="verlof">Verlof saldo</option>
                      <option value="flex">Flex uren (min)</option>
                      <option value="flex-plus">Flex uren (plus)</option>
                      <option value="geen">Geen aftrek</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button style={styles.saveBtn} disabled={!newVerlofType.key || !newVerlofType.text || !newVerlofType.label || processing}
                    onClick={async () => {
                      if (verlofTypes.find(t => t.key === newVerlofType.key)) { alert('Sleutel bestaat al!'); return; }
                      setProcessing(true);
                      try { await voegVerlofTypeToe(newVerlofType); setNewVerlofType(null); }
                      catch (err) { console.error(err); } finally { setProcessing(false); }
                    }}>Toevoegen</button>
                  <button style={styles.cancelBtn} onClick={() => setNewVerlofType(null)}>Annuleren</button>
                </div>
              </div>
            )}

            <div className="r-table-wrap">
            <table style={styles.table} className="r-table">
              <thead><tr>
                <th style={styles.th}>Volgorde</th>
                <th style={styles.th}>Sleutel</th>
                <th style={styles.th}>Naam</th>
                <th style={styles.th}>Agendalabel</th>
                <th style={styles.th}>Kleur</th>
                <th style={styles.th}>Aftrek saldo</th>
                <th style={styles.th}>Acties</th>
              </tr></thead>
              <tbody>
                {verlofTypes.map((t, idx) => editVerlofTypeId === t.id ? (
                  <tr key={t.id} style={{ ...styles.tr, background: '#faf9f8' }}>
                    <td style={styles.td}>
                      <input type="number" style={{ ...styles.editInput, width: 50 }} value={editVerlofTypeForm.sortOrder} onChange={e => setEditVerlofTypeForm({...editVerlofTypeForm, sortOrder: Number(e.target.value)})} />
                    </td>
                    <td style={styles.td}><code style={{ fontSize: 12, color: '#605e5c' }}>{t.key}</code></td>
                    <td style={styles.td}><input style={styles.editInput} value={editVerlofTypeForm.text} onChange={e => setEditVerlofTypeForm({...editVerlofTypeForm, text: e.target.value})} /></td>
                    <td style={styles.td}><input style={{ ...styles.editInput, width: 60 }} value={editVerlofTypeForm.label} onChange={e => setEditVerlofTypeForm({...editVerlofTypeForm, label: e.target.value.toUpperCase().slice(0, 4)})} maxLength={4} /></td>
                    <td style={styles.td}><input type="color" style={{ width: 40, height: 30, border: 'none', cursor: 'pointer' }} value={editVerlofTypeForm.color} onChange={e => setEditVerlofTypeForm({...editVerlofTypeForm, color: e.target.value})} /></td>
                    <td style={styles.td}>
                      <select style={{ ...styles.editInput, width: 110 }} value={editVerlofTypeForm.aftrekSaldo || 'verlof'}
                        onChange={e => setEditVerlofTypeForm({...editVerlofTypeForm, aftrekSaldo: e.target.value, trektAfVanVerlof: e.target.value === 'verlof'})}>
                        <option value="verlof">Verlof saldo</option>
                        <option value="flex">Flex uren (min)</option>
                        <option value="flex-plus">Flex uren (plus)</option>
                        <option value="geen">Geen aftrek</option>
                      </select>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionRow}>
                        <button style={styles.saveBtn} disabled={processing} onClick={async () => {
                          setProcessing(true);
                          try { await werkVerlofTypeBij(t.id, editVerlofTypeForm); setEditVerlofTypeId(null); }
                          catch (err) { console.error(err); } finally { setProcessing(false); }
                        }}>✓</button>
                        <button style={styles.cancelBtn} onClick={() => setEditVerlofTypeId(null)}>✕</button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={t.id || idx} style={styles.tr}>
                    <td style={styles.td}><span style={{ fontSize: 12, color: '#a19f9d' }}>{t.sortOrder ?? idx}</span></td>
                    <td style={styles.td}><code style={{ fontSize: 12, background: '#f3f2f1', padding: '2px 6px', borderRadius: 4 }}>{t.key}</code></td>
                    <td style={styles.td}><strong>{t.text}</strong></td>
                    <td style={styles.td}>
                      <span style={{ background: `${t.color || '#767676'}20`, color: t.color, padding: '3px 10px', borderRadius: 6, fontSize: 13, fontWeight: 700 }}>{t.label}</span>
                    </td>
                    <td style={styles.td}><div style={{ width: 24, height: 24, borderRadius: 6, background: t.color || '#767676' }} /></td>
                    <td style={styles.td}>
                      {(t.aftrekSaldo || 'verlof') === 'verlof'
                        ? <span style={{ color: '#D13438', fontSize: 12, fontWeight: 600 }}>Verlof saldo</span>
                        : (t.aftrekSaldo || 'verlof') === 'flex'
                          ? <span style={{ color: '#107C10', fontSize: 12, fontWeight: 600 }}>Flex uren (min)</span>
                          : (t.aftrekSaldo || 'verlof') === 'flex-plus'
                            ? <span style={{ color: '#00B7C3', fontSize: 12, fontWeight: 600 }}>Flex uren (plus)</span>
                            : <span style={{ color: '#a19f9d', fontSize: 12, fontWeight: 600 }}>Geen aftrek</span>
                      }
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionRow}>
                        <button style={styles.editBtn} onClick={() => {
                          setEditVerlofTypeId(t.id);
                          setEditVerlofTypeForm({ text: t.text, label: t.label, color: t.color, trektAfVanVerlof: t.trektAfVanVerlof !== false, aftrekSaldo: t.aftrekSaldo || 'verlof', sortOrder: t.sortOrder ?? idx });
                        }}>✏️</button>
                        <button style={styles.deleteBtn} onClick={async () => {
                          const inGebruik = verlofaanvragen.filter(v => v.Type === t.key).length;
                          if (inGebruik > 0) { alert(`Kan niet verwijderen: ${inGebruik} aanvragen gebruiken dit type.`); return; }
                          if (window.confirm(`Verloftype "${t.text}" verwijderen?`)) {
                            setProcessing(true);
                            try { await verwijderVerlofType(t.id); } catch (err) { console.error(err); } finally { setProcessing(false); }
                          }
                        }}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            <div style={{ marginTop: 16, padding: 12, background: '#f0f6ff', borderRadius: 8, fontSize: 12, color: '#605e5c', lineHeight: 1.6 }}>
              <strong>💡 Uitleg:</strong><br />
              <strong>Label</strong> — kort label dat in de kalender getoond wordt (max 4 tekens, bijv. V, Z, BV)<br />
              <strong>Trekt af van saldo</strong> — als "Ja" worden de uren afgetrokken van het verlofbudget. Zet op "Nee" voor bijv. ziekte, bijzonder verlof, etc.<br />
              <strong>Volgorde</strong> — bepaalt de volgorde in keuzemenu's (lager = eerder)
            </div>
          </div>
        </div>
      )}

      {activeTab === 'werkrooster' && (
        <div style={styles.section}>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Werkrooster per medewerker</h3>
            <p style={styles.hint}>
              Vul per dag de ochtend-, middag- en avondtijden in. Pauze 12:30–13:00 (30 min) is voor eigen rekening.
              Klik ✕ naast een tijdveld om het te wissen.
            </p>

            {medewerkers.filter(m => m.Rol !== 'flexkracht' && (m.Status || 'actief') === 'actief').map(m => (
              <div key={m.Id} style={{ marginBottom: 24, borderBottom: '1px solid #edebe9', paddingBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <strong style={{ fontSize: 15, color: '#1a1a2e' }}>{m.Naam}</strong>
                    <span style={{ fontSize: 12, color: '#a19f9d', marginLeft: 8 }}>{m.Afdeling}</span>
                  </div>
                  {roosterEditId !== m.Id ? (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button style={styles.editBtn} onClick={() => startRoosterEdit(m)}>✏️ Bewerken</button>
                      <select
                        style={{ fontSize: 11, padding: '4px 8px', border: '1px solid #0078D4', borderRadius: 6, background: '#0078D410', color: '#0078D4', fontWeight: 600, cursor: 'pointer' }}
                        value=""
                        onChange={async (e) => {
                          const bronId = e.target.value;
                          if (!bronId) return;
                          const bron = medewerkers.find(mw => mw.Id === bronId);
                          if (!bron?.Werkrooster) { alert('Geselecteerde medewerker heeft geen werkrooster.'); return; }
                          if (!window.confirm(`Werkrooster van ${bron.Naam} kopiëren naar ${m.Naam}?\n\nHet huidige rooster van ${m.Naam} wordt overschreven.`)) return;
                          setProcessing(true);
                          try {
                            await werkMedewerkerBij(m.Id, { werkrooster: { ...bron.Werkrooster } });
                            triggerOutlookWerkroosterSync();
                          } catch (err) { alert('Kopiëren mislukt: ' + err.message); }
                          setProcessing(false);
                        }}
                      >
                        <option value="">📋 Kopieer van...</option>
                        {medewerkers
                          .filter(mw => mw.Id !== m.Id && mw.Werkrooster && mw.Rol !== 'flexkracht')
                          .sort((a, b) => a.Naam.localeCompare(b.Naam))
                          .map(mw => (
                            <option key={mw.Id} value={mw.Id}>{mw.Naam}</option>
                          ))
                        }
                      </select>
                    </div>
                  ) : (
                    <div style={styles.actionRow}>
                      <button style={styles.saveBtn} onClick={saveRooster} disabled={processing}>Opslaan</button>
                      <button style={styles.cancelBtn} onClick={() => setRoosterEditId(null)}>Annuleren</button>
                    </div>
                  )}
                </div>

                {roosterEditId === m.Id ? (
                  <div className="r-table-wrap">
                  <table style={{ ...styles.table, fontSize: 13 }}>
                    <thead><tr>
                      <th style={styles.th}>Dag</th>
                      <th style={styles.th}>Ochtend start</th><th style={styles.th}>Ochtend eind</th>
                      <th style={styles.th}>Middag start</th><th style={styles.th}>Middag eind</th>
                      <th style={styles.th}>Avond start</th><th style={styles.th}>Avond eind</th>
                      <th style={styles.th}></th>
                    </tr></thead>
                    <tbody>
                      {DAGEN.map(dag => (
                        <tr key={dag} style={styles.tr}>
                          <td style={styles.td}><strong>{DAG_LABELS[dag]}</strong></td>
                          <td style={styles.td}><TimeInput value={roosterForm[dag]?.ochtendStart || ''} onChange={v => updateRoosterVeld(dag, 'ochtendStart', v)} onClear={() => updateRoosterVeld(dag, 'ochtendStart', '')} /></td>
                          <td style={styles.td}><TimeInput value={roosterForm[dag]?.ochtendEind || ''} onChange={v => updateRoosterVeld(dag, 'ochtendEind', v)} onClear={() => updateRoosterVeld(dag, 'ochtendEind', '')} /></td>
                          <td style={styles.td}><TimeInput value={roosterForm[dag]?.middagStart || ''} onChange={v => updateRoosterVeld(dag, 'middagStart', v)} onClear={() => updateRoosterVeld(dag, 'middagStart', '')} /></td>
                          <td style={styles.td}><TimeInput value={roosterForm[dag]?.middagEind || ''} onChange={v => updateRoosterVeld(dag, 'middagEind', v)} onClear={() => updateRoosterVeld(dag, 'middagEind', '')} /></td>
                          <td style={styles.td}><TimeInput value={roosterForm[dag]?.avondStart || ''} onChange={v => updateRoosterVeld(dag, 'avondStart', v)} onClear={() => updateRoosterVeld(dag, 'avondStart', '')} /></td>
                          <td style={styles.td}><TimeInput value={roosterForm[dag]?.avondEind || ''} onChange={v => updateRoosterVeld(dag, 'avondEind', v)} onClear={() => updateRoosterVeld(dag, 'avondEind', '')} /></td>
                          <td style={styles.td}>
                            <button style={{ border: 'none', background: '#fde7e9', color: '#D13438', borderRadius: 6, padding: '4px 8px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
                              onClick={() => { clearRoosterDeel(dag, 'ochtend'); clearRoosterDeel(dag, 'middag'); clearRoosterDeel(dag, 'avond'); }}
                              title="Hele dag wissen">🗑️</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                ) : (
                  <div className="r-table-wrap">
                  <table style={{ ...styles.table, fontSize: 13 }}>
                    <thead><tr><th style={styles.th}>Dag</th><th style={styles.th}>Ochtend</th><th style={styles.th}>Middag</th><th style={styles.th}>Avond</th></tr></thead>
                    <tbody>
                      {DAGEN.map(dag => {
                        const r = m.Werkrooster?.[dag];
                        const heeftDienst = r && (r.ochtend || r.middag || r.avond);
                        return (
                          <tr key={dag} style={{ ...styles.tr, opacity: heeftDienst ? 1 : 0.4 }}>
                            <td style={styles.td}><strong>{DAG_LABELS[dag]}</strong></td>
                            <td style={styles.td}>{r?.ochtend || '—'}</td>
                            <td style={styles.td}>{r?.middag || '—'}</td>
                            <td style={styles.td}>{r?.avond || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ==================== FLEX BEHEER (NEW) ==================== */}
      {activeTab === 'uitbetalen' && (
        <div style={styles.section}>
          {/* ---- FLEX URENOVERZICHT ---- */}
          {flexSaldi.length > 0 && (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>⚡ Flexkracht urenoverzicht {currentYear}</h3>
            <div className="r-table-wrap">
            <table style={styles.table} className="r-table">
              <thead><tr>
                <th style={styles.th}>Flexkracht</th><th style={styles.th}>Ingepland</th><th style={styles.th}>Gewerkt</th>
                <th style={styles.th}>Uitbetaald</th><th style={styles.th}>Openstaand</th>
              </tr></thead>
              <tbody>
                {flexSaldi.map(fk => (
                  <tr key={fk.Id} style={styles.tr}>
                    <td style={styles.td}><strong>{fk.Naam}</strong></td>
                    <td style={styles.td}>{fk.ingepland}u</td>
                    <td style={styles.td}>{fk.gewerkt}u</td>
                    <td style={styles.td}>{fk.uitbetaald}u</td>
                    <td style={styles.td}><strong style={{ color: fk.openstaand > 0 ? '#E86E1C' : '#107C10' }}>{fk.openstaand}u</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
          )}

          {/* ---- MEDEWERKER OVERURENOVERZICHT ---- */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>⏰ Medewerker overurenoverzicht {currentYear}</h3>
            {(() => {
              const medewerkersExAdmin = medewerkers.filter(m => (m.Status || 'actief') === 'actief' && !m.IsAdminOnly);
              const mwSaldi = medewerkersExAdmin.map(m => {
                const mijnOU = overuren.filter(o => o.medewerkerId === m.Id);
                const verdiend = mijnOU.filter(o => o.Status === 'goedgekeurd').reduce((s, o) => s + (o.Uren || 0), 0);
                const uitbetaald = mijnOU.filter(o => o.Status === 'uitbetaald').reduce((s, o) => s + (o.Uren || 0), 0);
                const ingediend = mijnOU.filter(o => o.Status === 'ingediend').reduce((s, o) => s + (o.Uren || 0), 0);
                // Overuren gebruikt als verlof
                const gebruiktVerlof = verlofaanvragen.filter(v => v.medewerkerId === m.Id && v.status === 'goedgekeurd' && v.jaar === currentYear)
                  .reduce((s, v) => s + (v.overurenGebruikt || 0), 0);
                const saldo = verdiend - gebruiktVerlof - uitbetaald;
                return { ...m, verdiend, uitbetaald, ingediend, gebruiktVerlof, saldo };
              }).filter(m => m.verdiend > 0 || m.uitbetaald > 0 || m.ingediend > 0 || m.saldo !== 0);
              if (mwSaldi.length === 0) return <p style={styles.hint}>Geen medewerkers met overuren.</p>;
              return (
                <div className="r-table-wrap">
                <table style={styles.table} className="r-table">
                  <thead><tr>
                    <th style={styles.th}>Medewerker</th><th style={styles.th}>Ingediend</th><th style={styles.th}>Goedgekeurd</th>
                    <th style={styles.th}>Verlof opgenomen</th><th style={styles.th}>Uitbetaald</th><th style={styles.th}>Saldo</th>
                  </tr></thead>
                  <tbody>
                    {mwSaldi.map(m => (
                      <tr key={m.Id} style={styles.tr}>
                        <td style={styles.td}><strong>{m.Naam}</strong></td>
                        <td style={styles.td}>{m.ingediend > 0 ? <span style={{ color: '#FFB900', fontWeight: 600 }}>{m.ingediend}u ⏳</span> : '—'}</td>
                        <td style={styles.td}>{m.verdiend}u</td>
                        <td style={styles.td}>{m.gebruiktVerlof > 0 ? `${m.gebruiktVerlof}u` : '—'}</td>
                        <td style={styles.td}>{m.uitbetaald > 0 ? <span style={{ color: '#8764B8' }}>{m.uitbetaald}u 💰</span> : '—'}</td>
                        <td style={styles.td}><strong style={{ color: m.saldo > 0 ? '#E86E1C' : m.saldo < 0 ? '#D13438' : '#107C10' }}>{m.saldo}u</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              );
            })()}
          </div>

          {/* ---- UITBETALEN / CORRECTIE FORMULIER ---- */}
          <div style={{ ...styles.card, border: '2px solid #8764B8' }}>
            <h3 style={{ ...styles.cardTitle, color: '#8764B8' }}>💰 Uitbetalen / Correctie</h3>
            <p style={styles.hint}>Betaal verlofuren, overuren of flex uren uit, of voer een saldo-correctie door.</p>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }} className="r-form-row">
              <div style={{ flex: 1, minWidth: 160 }}>
                <label style={styles.editLabel}>Categorie</label>
                <select style={styles.editInput} value={saldoForm.categorie} onChange={e => setSaldoForm({...saldoForm, categorie: e.target.value, medewerkerId: ''})}>
                  <option value="verlofuren">🏖️ Verlofuren medewerker</option>
                  <option value="overuren">⏰ Overuren medewerker</option>
                  {flexSaldi.length > 0 && <option value="flex">⚡ Flex uren</option>}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label style={styles.editLabel}>{saldoForm.categorie === 'flex' ? 'Flexkracht' : 'Medewerker'}</label>
                <select style={styles.editInput} value={saldoForm.medewerkerId} onChange={e => setSaldoForm({...saldoForm, medewerkerId: e.target.value})}>
                  <option value="" disabled>--- Kies ---</option>
                  {saldoForm.categorie === 'flex'
                    ? flexSaldi.map(f => <option key={f.Id} value={f.Id}>{f.Naam} (open: {f.openstaand}u)</option>)
                    : saldoForm.categorie === 'overuren'
                    ? (() => {
                        const todayStr = new Date().toISOString().split('T')[0];
                        return medewerkers.filter(m => (m.Status || 'actief') === 'actief').map(m => {
                          // Alleen verdiende overuren in het verleden (niet toekomstig)
                          const verdiend = overuren.filter(o => o.medewerkerId === m.Id && o.Status === 'goedgekeurd' && (o.datum || '') <= todayStr).reduce((s, o) => s + (o.Uren || 0), 0);
                          const uitbetaald = overuren.filter(o => o.medewerkerId === m.Id && o.Status === 'uitbetaald').reduce((s, o) => s + (o.Uren || 0), 0);
                          const gebruikt = verlofaanvragen.filter(v => v.medewerkerId === m.Id && v.status === 'goedgekeurd' && v.jaar === currentYear).reduce((s, v) => s + (v.overurenGebruikt || 0), 0);
                          const saldo = verdiend - uitbetaald - gebruikt;
                          if (saldo <= 0) return null; // Niet tonen als geen beschikbaar saldo
                          return <option key={m.Id} value={m.Id}>{m.Naam} (saldo: {saldo}u)</option>;
                        }).filter(Boolean);
                      })()
                    : medewerkers.filter(m => (m.Status || 'actief') === 'actief' && m.Rol !== 'flexkracht').map(m => {
                        const totaal = (m.BerekendeVerlofUren || 0) + (m.MeegenomenUren || m.meegenomenUren || 0);
                        const opgenomen = m.VerlofOpgenomen || 0;
                        const resterend = totaal - opgenomen;
                        return <option key={m.Id} value={m.Id}>{m.Naam} (resterend: {resterend}u)</option>;
                      })
                  }
                </select>
                {saldoForm.categorie === 'overuren' && (() => {
                  const todayStr = new Date().toISOString().split('T')[0];
                  const anyAvail = medewerkers.some(m => {
                    const v = overuren.filter(o => o.medewerkerId === m.Id && o.Status === 'goedgekeurd' && (o.datum || '') <= todayStr).reduce((s, o) => s + (o.Uren || 0), 0);
                    const u = overuren.filter(o => o.medewerkerId === m.Id && o.Status === 'uitbetaald').reduce((s, o) => s + (o.Uren || 0), 0);
                    return (v - u) > 0;
                  });
                  if (!anyAvail) return <div style={{ fontSize: 11, color: '#a19f9d', marginTop: 4 }}>Geen medewerkers met beschikbare overuren (alleen reeds gewerkte uren tellen mee)</div>;
                  return null;
                })()}
              </div>
              <div style={{ flex: 1, minWidth: 120 }}>
                <label style={styles.editLabel}>Type</label>
                <select style={styles.editInput} value={saldoForm.type} onChange={e => setSaldoForm({...saldoForm, type: e.target.value})}>
                  <option value="uitbetaling">💰 Uitbetaling</option>
                  <option value="correctie">📝 Correctie (+/-)</option>
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 100 }}>
                <label style={styles.editLabel}>Uren</label>
                <input type="number" style={styles.editInput} value={saldoForm.uren} step="0.5"
                  onChange={e => setSaldoForm({...saldoForm, uren: e.target.value})}
                  placeholder={saldoForm.type === 'correctie' ? 'bijv. -2 of +5' : 'bijv. 10'} />
              </div>
              <div style={{ flex: 2, minWidth: 200 }}>
                <label style={styles.editLabel}>Omschrijving</label>
                <input style={styles.editInput} value={saldoForm.omschrijving}
                  onChange={e => setSaldoForm({...saldoForm, omschrijving: e.target.value})}
                  placeholder="Bijv. uitbetaling januari, correctie..." />
              </div>
            </div>
            <button style={styles.addButton} onClick={handleSaldoAanpassing} disabled={processing}>
              {processing ? 'Bezig...' : '💰 Doorvoeren'}
            </button>
          </div>

          {/* ---- UITBETALINGSHISTORIE ---- */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>📋 Uitbetalings- & correctiehistorie</h3>
            {flexUitbetalingen.length === 0 ? <p style={styles.hint}>Nog geen mutaties.</p> : (
              <div className="r-table-wrap">
              <table style={styles.table} className="r-table">
                <thead><tr>
                  <th style={styles.th}>Naam</th><th style={styles.th}>Datum</th><th style={styles.th}>Type</th>
                  <th style={styles.th}>Uren</th><th style={styles.th}>Omschrijving</th>
                </tr></thead>
                <tbody>
                  {[...flexUitbetalingen].sort((a,b) => b.datum?.localeCompare(a.datum)).map(u => (
                    <tr key={u.id} style={styles.tr}>
                      <td style={styles.td}><strong>{u.flexkrachtNaam}</strong></td>
                      <td style={styles.td}>{fmtDatum(u.datum)}</td>
                      <td style={styles.td}><span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                        background: u.type === 'correctie' ? '#FFB90020' : u.type === 'overuren-uitbetaling' ? '#E86E1C20' : u.type === 'verlofuren-uitbetaling' ? '#0078D420' : '#8764B820',
                        color: u.type === 'correctie' ? '#FFB900' : u.type === 'overuren-uitbetaling' ? '#E86E1C' : u.type === 'verlofuren-uitbetaling' ? '#0078D4' : '#8764B8',
                      }}>{u.type === 'correctie' ? 'Correctie' : u.type === 'overuren-uitbetaling' ? 'Overuren' : u.type === 'verlofuren-uitbetaling' ? 'Verlofuren' : 'Flex'}</span></td>
                      <td style={styles.td}>{u.uren}u</td>
                      <td style={styles.td}>{u.omschrijving}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ==================== FEESTDAGEN ==================== */}
      {activeTab === 'feestdagen' && (
        <div style={styles.section}>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Feestdagen {currentYear}</h3>
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'flex-end' }} className="r-form-row">
              <div><label style={styles.editLabel}>Datum</label><input type="date" style={styles.editInput} value={newFeestdag.datum} onChange={e => setNewFeestdag({...newFeestdag, datum: e.target.value})} /></div>
              <div style={{ flex: 1 }}><label style={styles.editLabel}>Naam</label><input style={styles.editInput} value={newFeestdag.naam} placeholder="Bijv. Koningsdag" onChange={e => setNewFeestdag({...newFeestdag, naam: e.target.value})} /></div>
              <button style={styles.addButton} onClick={handleAddFeestdag}>Toevoegen</button>
            </div>
            <div className="r-table-wrap">
            <table style={styles.table} className="r-table">
              <thead><tr><th style={styles.th}>Datum</th><th style={styles.th}>Feestdag</th><th style={styles.th}>Dag</th><th style={styles.th}>Acties</th></tr></thead>
              <tbody>
                {[...feestdagen].sort((a,b) => (a.Datum||'').localeCompare(b.Datum||'')).map(f => {
                  const date = new Date(f.Datum + 'T12:00:00');
                  return (
                    <tr key={f.Id} style={styles.tr}>
                      <td style={styles.td}>{fmtDatum(f.Datum)}</td>
                      <td style={styles.td}><strong>{f.Naam}</strong></td>
                      <td style={{...styles.td, textTransform: 'capitalize'}}>{date.toLocaleDateString('nl-NL', { weekday: 'long' })}</td>
                      <td style={styles.td}><button style={styles.deleteBtn} onClick={() => verwijderFeestdag(f.Id)}>🗑️</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}

      {/* ==================== VAKANTIEPERIODEN ==================== */}
      {activeTab === 'vakantieperioden' && (
        <div style={styles.section}>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>🏖️ Vakantieperioden {currentYear}</h3>
            <p style={styles.hint}>Voeg nationale vakantieperioden toe (bijv. Kerstvakantie, Meivakantie). Medewerkers zien een melding bij verlofaanvragen in deze perioden. Perioden worden ook in de kalender weergegeven.</p>
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'flex-end', flexWrap: 'wrap' }} className="r-form-row">
              <div style={{ flex: 1, minWidth: 160 }}>
                <label style={styles.editLabel}>Naam</label>
                <input style={styles.editInput} value={newVakantiePeriode.naam} placeholder="Bijv. Kerstvakantie" onChange={e => setNewVakantiePeriode({...newVakantiePeriode, naam: e.target.value})} />
              </div>
              <div>
                <label style={styles.editLabel}>Startdatum</label>
                <input type="date" style={styles.editInput} value={newVakantiePeriode.startDatum} onChange={e => setNewVakantiePeriode({...newVakantiePeriode, startDatum: e.target.value})} />
              </div>
              <div>
                <label style={styles.editLabel}>Einddatum</label>
                <input type="date" style={styles.editInput} value={newVakantiePeriode.eindDatum} min={newVakantiePeriode.startDatum} onChange={e => setNewVakantiePeriode({...newVakantiePeriode, eindDatum: e.target.value})} />
              </div>
              <button style={styles.addButton} onClick={handleAddVakantiePeriode}>Toevoegen</button>
            </div>
            {vakantiePerioden.length === 0 ? <p style={styles.hint}>Geen vakantieperioden ingesteld.</p> : (
              <div className="r-table-wrap">
              <table style={styles.table} className="r-table">
                <thead><tr><th style={styles.th}>Vakantie</th><th style={styles.th}>Van</th><th style={styles.th}>Tot</th><th style={styles.th}>Dagen</th><th style={styles.th}>Acties</th></tr></thead>
                <tbody>
                  {[...vakantiePerioden].sort((a,b) => (a.startDatum||'').localeCompare(b.startDatum||'')).map(vp => {
                    const start = new Date(vp.startDatum + 'T12:00:00');
                    const eind = new Date(vp.eindDatum + 'T12:00:00');
                    const dagen = Math.round((eind - start) / (1000*60*60*24)) + 1;
                    return (
                      <tr key={vp.Id} style={styles.tr}>
                        <td style={styles.td}><strong>🏖️ {vp.naam}</strong></td>
                        <td style={styles.td}>{fmtDatum(vp.startDatum)}</td>
                        <td style={styles.td}>{fmtDatum(vp.eindDatum)}</td>
                        <td style={styles.td}>{dagen} dag{dagen > 1 ? 'en' : ''}</td>
                        <td style={styles.td}><button style={styles.deleteBtn} onClick={() => verwijderVakantiePeriode(vp.Id)}>🗑️</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ==================== ROOSTERWIJZIGINGEN ==================== */}
      {activeTab === 'roosterwijzigingen' && (
        <div style={styles.section}>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>🔄 Roosterwijzigingen</h3>
            <p style={styles.hint}>Registreer wanneer een medewerker op een andere dag werkt. Vul optioneel een 'vervalt datum' in — die standaard werkdag wordt dan doorgestreept in de kalender. Het standaard rooster hervat automatisch de week erna.</p>
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'flex-end', flexWrap: 'wrap' }} className="r-form-row">
              <div style={{ flex: 1, minWidth: 160 }}>
                <label style={styles.editLabel}>Medewerker</label>
                <select style={styles.editInput} value={newRoosterWijziging.medewerkerId} onChange={e => setNewRoosterWijziging({...newRoosterWijziging, medewerkerId: e.target.value})}>
                  <option value="" disabled>--- Kies ---</option>
                  {medewerkers.filter(m => (m.Status || 'actief') === 'actief').map(m => <option key={m.Id} value={m.Id}>{m.Naam}</option>)}
                </select>
              </div>
              <div>
                <label style={styles.editLabel}>Werkt op (nieuwe dag)</label>
                <input type="date" style={styles.editInput} value={newRoosterWijziging.datum} onChange={e => setNewRoosterWijziging({...newRoosterWijziging, datum: e.target.value})} />
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={newRoosterWijziging.heleDag} onChange={e => setNewRoosterWijziging({...newRoosterWijziging, heleDag: e.target.checked, startTijd: '', eindTijd: ''})} />
                  Hele dag
                </label>
              </div>
              {!newRoosterWijziging.heleDag && (
                <>
                  <div>
                    <label style={styles.editLabel}>Starttijd</label>
                    <input type="time" style={styles.editInput} value={newRoosterWijziging.startTijd} onChange={e => setNewRoosterWijziging({...newRoosterWijziging, startTijd: e.target.value})} />
                  </div>
                  <div>
                    <label style={styles.editLabel}>Eindtijd</label>
                    <input type="time" style={styles.editInput} value={newRoosterWijziging.eindTijd} onChange={e => setNewRoosterWijziging({...newRoosterWijziging, eindTijd: e.target.value})} />
                  </div>
                  {newRoosterWijziging.startTijd && newRoosterWijziging.eindTijd && (
                    <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
                      <div style={{ background: '#f0f6ff', border: '1px solid #c7e0f4', borderRadius: 8, padding: '6px 12px', fontSize: 14, fontWeight: 700, color: '#0078D4', whiteSpace: 'nowrap' }}>
                        = {Math.round(berekenUrenMetPauze(newRoosterWijziging.startTijd, newRoosterWijziging.eindTijd).netto * 100) / 100}u
                      </div>
                    </div>
                  )}
                </>
              )}
              <div>
                <label style={styles.editLabel}>Vervalt roosterdag (optioneel)</label>
                <input type="date" style={styles.editInput} value={newRoosterWijziging.vervangDatum}
                  onChange={e => setNewRoosterWijziging({...newRoosterWijziging, vervangDatum: e.target.value})} />
                <div style={{ fontSize: 10, color: '#605e5c', marginTop: 2 }}>Wordt doorgestreept in kalender</div>
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <label style={styles.editLabel}>Omschrijving (optioneel)</label>
                <input style={styles.editInput} value={newRoosterWijziging.omschrijving} placeholder="Bijv. Ruil wo→do" onChange={e => setNewRoosterWijziging({...newRoosterWijziging, omschrijving: e.target.value})} />
              </div>
              <button style={styles.addButton} onClick={handleAddRoosterWijziging}>Toevoegen</button>
            </div>
            {roosterWijzigingen.length === 0 ? <p style={styles.hint}>Geen roosterwijzigingen geregistreerd.</p> : (
              <div className="r-table-wrap">
              <table style={styles.table} className="r-table">
                <thead><tr><th style={styles.th}>Medewerker</th><th style={styles.th}>Werkt op</th><th style={styles.th}>Tijden</th><th style={styles.th}>Uren</th><th style={styles.th}>Vervalt</th><th style={styles.th}>Omschrijving</th><th style={styles.th}>Acties</th></tr></thead>
                <tbody>
                  {[...roosterWijzigingen].sort((a,b) => (a.datum||'').localeCompare(b.datum||'')).map(rw => (
                    <tr key={rw.Id} style={styles.tr}>
                      <td style={styles.td}><strong>{rw.medewerkerNaam}</strong></td>
                      <td style={styles.td}>{fmtDatum(rw.datum)}</td>
                      <td style={styles.td}>{rw.heleDag ? 'Hele dag' : `${rw.startTijd} – ${rw.eindTijd}`}</td>
                      <td style={styles.td}>{rw.heleDag ? '—' : `${Math.round(berekenUrenMetPauze(rw.startTijd || '', rw.eindTijd || '').netto * 100) / 100}u`}</td>
                      <td style={styles.td}>{rw.vervangDatum ? <span style={{ textDecoration: 'line-through', color: '#D13438' }}>{fmtDatum(rw.vervangDatum)}</span> : '—'}</td>
                      <td style={styles.td}><span style={{ fontSize: 12, color: '#605e5c' }}>{rw.omschrijving || '—'}</span></td>
                      <td style={styles.td}><button style={styles.deleteBtn} onClick={() => verwijderRoosterWijziging(rw.Id)}>🗑️</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ==================== ARCHIEF ==================== */}
      {activeTab === 'archief' && (
        <div style={styles.section}>
          <div style={styles.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={styles.cardTitle}>Jaararchief</h3>
              <button style={styles.addButton} onClick={handleJaarafsluiting} disabled={processing}>
                {processing ? 'Bezig...' : `Jaarafsluiting ${currentYear - 1}`}
              </button>
            </div>
            <p style={styles.hint}>Bij jaarafsluiting worden verlofuren pro-rata berekend op basis van contract en ingangsdatum. <strong>Meegenomen uren worden niet automatisch overgenomen</strong> — stel deze handmatig in via Medewerkers &gt; Meegenomen uren instellen.</p>
            {jaararchief.length === 0 ? <p style={styles.hint}>Nog geen jaarafsluiting uitgevoerd.</p> : (
              [...new Set(jaararchief.map(a => a.jaar))].sort((a,b) => b - a).map(jaar => (
                <div key={jaar} style={{ marginBottom: 24 }}>
                  <h4 style={{ fontSize: 16, fontWeight: 600, color: '#1a1a2e', marginBottom: 12 }}>Jaar {jaar}</h4>
                  <div className="r-table-wrap">
                  <table style={styles.table} className="r-table">
                    <thead><tr><th style={styles.th}>Medewerker</th><th style={styles.th}>Contract</th><th style={styles.th}>Meegen.</th><th style={styles.th}>Opgenomen</th><th style={styles.th}>Overuren</th><th style={styles.th}>Eindsaldo</th></tr></thead>
                    <tbody>{jaararchief.filter(a => a.jaar === jaar).map(a => (
                      <tr key={a.id} style={styles.tr}>
                        <td style={styles.td}><strong>{a.medewerkerNaam}</strong></td>
                        <td style={styles.td}>{a.contractUren}u</td><td style={styles.td}>{a.meegenomenUren}u</td>
                        <td style={styles.td}>{a.opgenomenUren}u</td><td style={styles.td}>{a.overurenSaldo}u</td>
                        <td style={styles.td}><strong>{a.eindsaldo}u</strong></td>
                      </tr>
                    ))}</tbody>
                  </table>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ==================== MENU CONFIGURATIE ==================== */}
      {activeTab === 'menu' && (() => {
        const defaultItems = [
          { key: '/', icon: '📊', label: 'Dashboard' },
          { key: '/verlof-overzicht', icon: '📋', label: 'Verlofoverzicht' },
          { key: '/verlof-medewerkers', icon: '👤', label: 'Verlofaanvraag medewerkers' },
          { key: '/indienen', icon: '📝', label: 'Indienen' },
          { key: '/verlof-aanvragen', icon: '✈️', label: 'Verlof' },
          { key: '/ziekmelding', icon: '🏥', label: 'Ziekmelding' },
          { key: '/overuren', icon: '⏰', label: 'Overuren' },
          { key: '/kalender', icon: '📅', label: 'Kalender' },
          { key: '/flex', icon: '⚡', label: 'Planning Flexkracht' },
          { key: '/admin', icon: '⚙️', label: 'Beheer' },
          { key: '/handleiding', icon: '📖', label: 'Handleiding' },
        ];
        const merged = defaultItems.map((d, i) => {
          const mc = (menuConfig || []).find(c => c.key === d.key);
          return { ...d, customLabel: mc?.label || '', order: mc?.order ?? i };
        }).sort((a, b) => a.order - b.order);
        return (
        <div style={styles.section}>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>📋 Menu-items aanpassen</h3>
            <p style={styles.hint}>Pas de namen van menu-items aan en wijzig de volgorde. Wijzigingen gelden voor alle gebruikers. Laat het veld leeg om de standaardnaam te gebruiken.</p>
            <div className="r-table-wrap">
            <table style={styles.table} className="r-table">
              <thead><tr><th style={styles.th}>Volgorde</th><th style={styles.th}>Route</th><th style={styles.th}>Standaard naam</th><th style={styles.th}>Aangepaste naam</th><th style={styles.th}>Verplaatsen</th></tr></thead>
              <tbody>
                {merged.map((item, idx) => (
                  <tr key={item.key} style={styles.tr}>
                    <td style={styles.td}><span style={{ fontWeight: 600, color: '#605e5c' }}>{idx + 1}</span></td>
                    <td style={styles.td}><code style={{ fontSize: 11, background: '#f3f2f1', padding: '2px 6px', borderRadius: 4 }}>{item.icon} {item.key}</code></td>
                    <td style={styles.td}><span style={{ color: '#a19f9d' }}>{item.label}</span></td>
                    <td style={styles.td}>
                      <input style={{ ...styles.editInput, width: '100%' }} value={item.customLabel}
                        placeholder={item.label}
                        onChange={e => {
                          const updated = merged.map((m, mi) => mi === idx ? { ...m, customLabel: e.target.value } : m);
                          const items = updated.map((m, mi) => ({ key: m.key, label: m.customLabel, order: mi }));
                          saveMenuConfig(items);
                        }} />
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button style={{ ...styles.editBtn, opacity: idx === 0 ? 0.3 : 1 }} disabled={idx === 0}
                          onClick={() => {
                            const arr = [...merged];
                            [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
                            saveMenuConfig(arr.map((m, mi) => ({ key: m.key, label: m.customLabel, order: mi })));
                          }}>⬆️</button>
                        <button style={{ ...styles.editBtn, opacity: idx === merged.length - 1 ? 0.3 : 1 }} disabled={idx === merged.length - 1}
                          onClick={() => {
                            const arr = [...merged];
                            [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
                            saveMenuConfig(arr.map((m, mi) => ({ key: m.key, label: m.customLabel, order: mi })));
                          }}>⬇️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div style={{ marginTop: 12 }}>
              <button style={styles.deleteBtn} onClick={() => { if (window.confirm('Alle menu-aanpassingen resetten naar standaard?')) saveMenuConfig([]); }}>
                🔄 Reset naar standaard
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ==================== INSTELLINGEN ==================== */}
      {activeTab === 'instellingen' && (
        <div style={styles.section}>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>App-instellingen</h3>
            <div style={styles.settingsList}>
              <div style={styles.settingItem} className="r-setting-item">
                <div><div style={styles.settingLabel}>Standaard contracturen</div><div style={styles.settingDesc}>Weekuren × 5 = jaarverlofuren (40u/week = 200u verlof)</div></div>
                <div style={styles.settingValue}>40u/week → 200u verlof</div>
              </div>
              <div style={styles.settingItem} className="r-setting-item">
                <div><div style={styles.settingLabel}>Pauze (eigen rekening)</div><div style={styles.settingDesc}>12:30–13:00 op ma–vr. Zaterdag: geen pauze</div></div>
                <div style={styles.settingValue}>30 min</div>
              </div>
              <div style={styles.settingItem} className="r-setting-item">
                <div><div style={styles.settingLabel}>Verlof labels in agenda</div><div style={styles.settingDesc}>Andere medewerkers zien alleen dit label (niet het volledige type)</div></div>
                <div style={styles.settingTags}>
                  {verlofTypes.map(t => (
                    <span key={t.key} style={{ ...styles.tag, background: `${t.color}15`, color: t.color }}>
                      {t.label} = {t.text} {t.trektAfVanVerlof !== false ? '(aftrek)' : '(geen aftrek)'}
                    </span>
                  ))}
                </div>
              </div>
              <div style={styles.settingItem} className="r-setting-item">
                <div><div style={styles.settingLabel}>Rollen</div></div>
                <div style={styles.settingTags}>
                  {sharepointConfig.rollen.map(r => <span key={r.key} style={{ ...styles.tag, background: `${r.color}15`, color: r.color }}>{r.icon} {r.text}</span>)}
                </div>
              </div>
              <div style={styles.settingItem} className="r-setting-item">
                <div><div style={styles.settingLabel}>In-app notificaties</div><div style={styles.settingDesc}>Medewerkers ontvangen automatisch meldingen bij statuswijzigingen</div></div>
                <div style={styles.settingValue}>Actief (🔔)</div>
              </div>
              <div style={styles.settingItem} className="r-setting-item">
                <div>
                  <div style={styles.settingLabel}>📱 Push notificaties (iOS/Android)</div>
                  <div style={styles.settingDesc}>
                    Vereist: 1) VAPID Key hieronder, 2) FCM_SERVER_KEY in Vercel env vars, 3) App geïnstalleerd op apparaat
                  </div>
                  <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input type="text" placeholder="VAPID Key (Firebase Console → Cloud Messaging)" 
                      style={{ ...styles.input, fontSize: 11, padding: '6px 10px', flex: 1, minWidth: 200 }}
                      defaultValue={localStorage.getItem('slaaploods_vapid_display') || ''}
                      onBlur={async (e) => {
                        const val = e.target.value.trim();
                        localStorage.setItem('slaaploods_vapid_display', val);
                        try {
                          const { collection: col, doc, setDoc, query: q, where: w, getDocs: gd } = await import('firebase/firestore');
                          const { db: database } = await import('../config/firebase');
                          const snap = await gd(q(col(database, 'settings'), w('key', '==', 'vapidKey')));
                          if (snap.empty) {
                            await setDoc(doc(col(database, 'settings')), { key: 'vapidKey', value: val });
                          } else {
                            const { updateDoc } = await import('firebase/firestore');
                            await updateDoc(snap.docs[0].ref, { value: val });
                          }
                        } catch (err) { console.warn('VAPID opslaan:', err); }
                      }}
                    />
                  </div>
                  <div style={{ fontSize: 10, color: '#a19f9d', marginTop: 4 }}>
                    Firebase Console → Project Settings → Cloud Messaging → Web Push certificates → Key pair
                  </div>
                </div>
              </div>
              <div style={styles.settingItem} className="r-setting-item">
                <div><div style={styles.settingLabel}>Data opslag</div></div>
                <div style={styles.settingValue}>Firebase / Firestore</div>
              </div>
            </div>
          </div>

          {/* ===== OUTLOOK AGENDA SYNC ===== */}
          <div style={{ ...styles.card, border: '2px solid #0078D4' }}>
            <h3 style={{ ...styles.cardTitle, color: '#0078D4' }}>📅 Outlook Agenda Sync</h3>
            <p style={{ fontSize: 13, color: '#605e5c', marginBottom: 16, lineHeight: 1.6 }}>
              Koppel een Microsoft 365 account (bijv. verlof@slaaploods.nl) om verlofaanvragen automatisch als agenda-items aan te maken.
              Medewerkers kunnen deze agenda abonneren in hun eigen Outlook.
            </p>

            {/* Status indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, padding: '12px 16px', borderRadius: 10,
              background: outlookStatus.connected ? '#dff6dd' : '#faf9f8', border: `1px solid ${outlookStatus.connected ? '#a7e3a5' : '#edebe9'}` }}>
              <span style={{ fontSize: 20 }}>{outlookStatus.loading ? '⏳' : outlookStatus.connected ? '🟢' : '⚪'}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: outlookStatus.connected ? '#107C10' : '#605e5c' }}>
                  {outlookStatus.loading ? 'Controleren...' : outlookStatus.connected ? `Verbonden: ${outlookStatus.account}` : 'Niet verbonden'}
                </div>
                {outlookStatus.connected && outlookStatus.name && (
                  <div style={{ fontSize: 12, color: '#605e5c' }}>{outlookStatus.name}</div>
                )}
                {outlookStatus.connected && outlookStatus.calendarName && (
                  <div style={{ fontSize: 11, color: '#0078D4', marginTop: 2 }}>📅 Agenda: {outlookStatus.calendarName}</div>
                )}
              </div>
            </div>

            {outlookMsg && (
              <div style={{ padding: '8px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13,
                background: outlookMsg.startsWith('✅') ? '#dff6dd' : outlookMsg.startsWith('❌') ? '#fde7e9' : '#fff4ce',
                color: outlookMsg.startsWith('✅') ? '#107C10' : outlookMsg.startsWith('❌') ? '#D13438' : '#7A6400' }}>
                {outlookMsg}
              </div>
            )}

            {/* Azure AD configuratie */}
            <div style={{ background: '#f0f6ff', border: '1px solid #c7e0f4', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0078D4', marginBottom: 12 }}>🔧 Azure AD App Registration</div>
              <div className="r-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={styles.editLabel}>Application (client) ID</label>
                  <input style={styles.editInput} value={azureForm.clientId} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    onChange={e => setAzureForm(f => ({...f, clientId: e.target.value.trim()}))} />
                </div>
                <div>
                  <label style={styles.editLabel}>Directory (tenant) ID</label>
                  <input style={styles.editInput} value={azureForm.tenantId} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    onChange={e => setAzureForm(f => ({...f, tenantId: e.target.value.trim()}))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button style={{ ...styles.saveBtn, padding: '6px 16px', fontSize: 12 }} onClick={handleAzureSave}
                  disabled={!azureForm.clientId || !azureForm.tenantId}>
                  💾 Opslaan
                </button>
                {azureForm.clientId && azureForm.tenantId && !outlookStatus.connected && (
                  <button style={{ ...styles.saveBtn, padding: '6px 16px', fontSize: 12, background: '#0078D4' }} onClick={handleOutlookConnect}>
                    🔗 Verbinden met Outlook
                  </button>
                )}
                {outlookStatus.connected && (
                  <>
                    <button style={{ ...styles.saveBtn, padding: '6px 16px', fontSize: 12, background: '#107C10' }} onClick={handleOutlookTest}>
                      🧪 Test verbinding
                    </button>
                    <button style={{ ...styles.addButton, padding: '6px 16px', fontSize: 12, background: '#D13438' }} onClick={handleOutlookDisconnect}>
                      ✕ Verbinding verbreken
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Sync alle items naar Outlook */}
            {outlookStatus.connected && (
              <div style={{ background: '#f0f6ff', border: '1px solid #c8ddf5', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <div style={{ fontWeight: 700, color: '#0078D4', marginBottom: 8, fontSize: 14 }}>
                  🔄 Synchronisatie
                </div>
                <p style={{ fontSize: 13, color: '#605e5c', marginBottom: 12, lineHeight: 1.5 }}>
                  Synchroniseer alle verlofaanvragen van dit jaar naar de Outlook agenda
                  "{outlookStatus.calendarName || 'Personeel- Verlofplanning'}". Bestaande items worden bijgewerkt, nieuwe worden aangemaakt.
                </p>
                {syncProgress ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <div style={{ flex: 1, height: 8, background: '#e1dfdd', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: '#0078D4', borderRadius: 4, transition: 'width 0.3s',
                          width: syncProgress.total > 0 ? `${(syncProgress.current / syncProgress.total) * 100}%` : '0%' }} />
                      </div>
                      <span style={{ fontSize: 12, color: '#605e5c', fontWeight: 600, minWidth: 50, textAlign: 'right' }}>
                        {syncProgress.current}/{syncProgress.total}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#605e5c' }}>
                      ⏳ {syncProgress.item?.naam
                        ? `${syncProgress.item?.naam || ''} — ${syncProgress.item?.type || syncProgress.item?.actie || ''}`
                        : syncProgress.item?.actie || 'bezig...'}
                    </div>
                  </div>
                ) : (
                  <>
                  {/* Werkrooster sync knoppen */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#107C10', marginBottom: 6 }}>📅 Werkrooster synchroniseren</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button style={{ ...styles.saveBtn, padding: '7px 16px', fontSize: 12, background: '#107C10' }}
                        onClick={() => {
                          const d = new Date(); d.setMonth(d.getMonth() + 6);
                          handleWerkroosterSync(d.toISOString().split('T')[0], '6 maanden');
                        }}>
                        📅 Werkrooster (6 maanden)
                      </button>
                      <button style={{ ...styles.saveBtn, padding: '7px 16px', fontSize: 12, background: '#0b6a0b' }}
                        onClick={() => {
                          const vj = new Date().getFullYear() + 1;
                          handleWerkroosterSync(`${vj}-12-31`, `t/m ${vj}`);
                        }}>
                        📅 Werkrooster (t/m {new Date().getFullYear() + 1})
                      </button>
                    </div>
                  </div>

                  {/* Mutatie sync knoppen */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#0078D4', marginBottom: 6 }}>📝 Mutaties synchroniseren (verlof, overuren, flex)</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button style={{ ...styles.saveBtn, padding: '7px 16px', fontSize: 12, background: '#0078D4' }}
                        onClick={() => {
                          const d = new Date(); d.setMonth(d.getMonth() + 6);
                          handleMutatieSync(d.toISOString().split('T')[0], '6 maanden');
                        }}>
                        📝 Mutaties (6 maanden)
                      </button>
                      <button style={{ ...styles.saveBtn, padding: '7px 16px', fontSize: 12, background: '#005a9e' }}
                        onClick={() => {
                          const vj = new Date().getFullYear() + 1;
                          handleMutatieSync(`${vj}-12-31`, `t/m ${vj}`);
                        }}>
                        📝 Mutaties (t/m {new Date().getFullYear() + 1})
                      </button>
                    </div>
                  </div>

                  {/* Alles verwijderen */}
                  <div style={{ borderTop: '1px solid #edebe9', paddingTop: 10 }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button style={{ ...styles.saveBtn, padding: '7px 16px', fontSize: 12, background: '#D83B01' }}
                        onClick={handleDeleteAll}>
                        🗑️ Alles verwijderen (werkrooster + mutaties)
                      </button>
                    </div>
                    <div style={{ fontSize: 11, color: '#a19f9d', marginTop: 4 }}>Verwijdert alle Outlook events. Gebruik dit bij problemen met dubbele of verouderde events.</div>
                  </div>
                  </>
                )}
                {syncResult && !syncResult.error && (
                  <div style={{ marginTop: 12, background: '#dff6dd', border: '1px solid #a7e3a5', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#107C10' }}>
                    ✅ <strong>{syncResult.deleteAll ? 'Alles verwijderd!' : `Sync voltooid${syncResult.label ? ` (${syncResult.label})` : ''}!`}</strong>
                    <div style={{ fontSize: 12, marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {syncResult.purged > 0 && <span style={{ color: '#D83B01' }}>🧹 {syncResult.purged} gewist</span>}
                      {syncResult.created > 0 && <span>➕ {syncResult.created} aangemaakt</span>}
                      {syncResult.mutaties && <span>📋 {syncResult.verlof || 0} verlof · ⏰ {syncResult.overuren || 0} overuren · ⚡ {syncResult.flex || 0} flex</span>}
                      {syncResult.errors > 0 && <span style={{ color: '#D13438' }}>⚠️ {syncResult.errors} fouten</span>}
                    </div>
                  </div>
                )}
                {syncResult?.error && (
                  <div style={{ marginTop: 12, background: '#fde7e9', border: '1px solid #f1b1b5', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#D13438' }}>
                    ❌ Sync mislukt: {syncResult.error}
                  </div>
                )}
              </div>
            )}
            <div style={{ background: '#faf9f8', borderRadius: 10, padding: 16, fontSize: 13, lineHeight: 1.8, color: '#605e5c' }}>
              <div style={{ fontWeight: 700, color: '#323130', marginBottom: 8 }}>Hoe werkt het?</div>
              <div>📝 <strong>Ingediend</strong> → agenda-item als <em>voorlopig</em> (tentative)</div>
              <div>✅ <strong>Goedgekeurd</strong> → agenda-item wordt <em>niet op kantoor</em> (out of office)</div>
              <div>❌ <strong>Afgekeurd / Ingetrokken</strong> → agenda-item wordt verwijderd</div>
              <div>✏️ <strong>Gewijzigd</strong> → agenda-item wordt bijgewerkt</div>
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #edebe9' }}>
                <div style={{ fontWeight: 700, color: '#323130', marginBottom: 8 }}>📅 Werkrooster sync:</div>
                <div>🟢 <strong>Beschikbaar</strong> → werktijden uit rooster (bijv. 08:30-17:00)</div>
                <div>🔴 <strong>Verlof</strong> → type verlof (vakantie, bijzonder, etc.)</div>
                <div>🟡 <strong>Dagdeel verlof</strong> → werktijden + verloftype gecombineerd</div>
                <div>🏥 <strong>Ziek</strong> → ziekmelding</div>
                <div>📋 <strong>Roosterwijziging</strong> → dag-ruil met reden</div>
                <div style={{ fontSize: 11, color: '#a19f9d', marginTop: 4 }}>Events krijgen categorie "Werkrooster" + medewerker-kleur. Bestaande werkrooster-events worden automatisch eerst verwijderd.</div>
              </div>
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #edebe9' }}>
                <div style={{ fontWeight: 700, color: '#323130', marginBottom: 8 }}>Azure instellen (eenmalig):</div>
                <div>1. Ga naar <strong>portal.azure.com</strong> → Azure Active Directory → App registrations</div>
                <div>2. Klik <strong>"New registration"</strong>:</div>
                <div style={{ paddingLeft: 16 }}>• Naam: <em>Slaaploods Verlofbeheer</em></div>
                <div style={{ paddingLeft: 16 }}>• Supported account types: <em>Single tenant</em></div>
                <div style={{ paddingLeft: 16 }}>• Redirect URI: <em>Single-page application (SPA)</em> → <code style={{ background: '#e8e8e8', padding: '2px 6px', borderRadius: 4 }}>{window.location.origin}</code></div>
                <div>3. Kopieer de <strong>Application (client) ID</strong> en <strong>Directory (tenant) ID</strong> hierboven</div>
                <div>4. Ga naar <strong>API permissions</strong> → Add → Microsoft Graph → Delegated:</div>
                <div style={{ paddingLeft: 16 }}>• <code>Calendars.ReadWrite</code></div>
                <div style={{ paddingLeft: 16 }}>• <code>User.Read</code></div>
                <div>5. Klik <strong>"Grant admin consent"</strong></div>
                <div>6. Klik hierboven op <strong>"Verbinden met Outlook"</strong> en log in met het service-account</div>
              </div>
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #edebe9' }}>
                <div style={{ fontWeight: 700, color: '#323130', marginBottom: 4 }}>💡 Tip: gedeelde agenda</div>
                <div>Log in met een gedeeld account (bijv. verlof@slaaploods.nl). Medewerkers kunnen deze agenda abonneren in hun Outlook zonder eigen Microsoft-account.</div>
              </div>
            </div>
          </div>
          {/* ===== IMPORT / EXPORT ===== */}
          <div style={{ ...styles.card, border: '2px solid #107C10' }}>
            <h3 style={{ ...styles.cardTitle, color: '#107C10' }}>📦 Data Import & Export</h3>

            {/* ---- EXPORT ---- */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>📤 Exporteren</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }} className="r-export-btns">
                <button style={{ ...styles.saveBtn, padding: '8px 16px', fontSize: 12, background: '#107C10' }}
                  onClick={() => exportData({
                    medewerkers, verlofaanvragen, overuren, feestdagen, vakantiePerioden,
                    verlofTypes, roosterWijzigingen, flexPlanning: [], flexUren: [], flexUitbetalingen: [], jaararchief
                  })}>
                  📤 Alles (JSON)
                </button>
                <button style={{ ...styles.saveBtn, padding: '8px 16px', fontSize: 12, background: '#0078D4' }}
                  onClick={() => exportVerlofCSV(verlofaanvragen, verlofTypes)}>
                  📋 Verlof/Ziek (CSV)
                </button>
                <button style={{ ...styles.saveBtn, padding: '8px 16px', fontSize: 12, background: '#E86E1C' }}
                  onClick={() => exportOverurenCSV(overuren)}>
                  ⏰ Overuren (CSV)
                </button>
                <button style={{ ...styles.saveBtn, padding: '8px 16px', fontSize: 12, background: '#8764B8' }}
                  onClick={() => exportFlexPlanningCSV(flexPlanning)}>
                  ⚡ Flex planning (CSV)
                </button>
                <button style={{ ...styles.saveBtn, padding: '8px 16px', fontSize: 12, background: '#605e5c' }}
                  onClick={() => exportMedewerkersCSV(medewerkers, verlofaanvragen, overuren, verlofTypes, currentYear)}>
                  📊 Medewerkers {currentYear} (CSV)
                </button>
                <button style={{ ...styles.saveBtn, padding: '8px 16px', fontSize: 12, background: '#0078D4' }}
                  onClick={() => {
                    const y = currentYear;
                    setPdfDialog({ vanDatum: `${y}-01-01`, totDatum: `${y}-12-31`, medewerkerId: 'alle' });
                  }}>
                  📄 PDF {currentYear}
                </button>
              </div>
            </div>

            {/* ---- IMPORT ---- */}
            <div style={{ background: '#f0fff0', border: '1px solid #a7e3a5', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#107C10', marginBottom: 4 }}>📥 CSV Importeren</div>
              <p style={{ fontSize: 12, color: '#605e5c', marginBottom: 12, lineHeight: 1.5 }}>
                Importeer verlof-, overuren- of flex-mutaties via een CSV-bestand. Medewerkers worden automatisch gekoppeld op naam.
                Datums kunnen in dd-mm-jjjj of jjjj-mm-dd formaat.
              </p>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#605e5c', alignSelf: 'center' }}>Templates:</span>
                <button style={{ ...styles.cancelBtn, padding: '4px 10px', fontSize: 11 }} onClick={() => downloadTemplate('verlof')}>📋 Verlof</button>
                <button style={{ ...styles.cancelBtn, padding: '4px 10px', fontSize: 11 }} onClick={() => downloadTemplate('overuren')}>⏰ Overuren</button>
                <button style={{ ...styles.cancelBtn, padding: '4px 10px', fontSize: 11 }} onClick={() => downloadTemplate('flex')}>⚡ Flex</button>
              </div>

              <input type="file" accept=".csv,.txt" ref={csvImportRef} style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file || !csvImportRef.current?.dataset?.importType) return;
                  const importType = csvImportRef.current.dataset.importType;
                  try {
                    const text = await leesCSVBestand(file);
                    let result;
                    if (importType === 'verlof') {
                      result = parseVerlofCSV(text, medewerkers, verlofTypes, currentUser);
                    } else if (importType === 'overuren') {
                      result = parseOverurenCSV(text, medewerkers);
                    } else if (importType === 'flex') {
                      result = parseFlexPlanningCSV(text, medewerkers);
                    }
                    setCsvImport({ type: importType, ...result });
                    setCsvImportResult(null);
                  } catch (err) {
                    setCsvImport({ type: importType, records: [], warnings: [], errors: [err.message] });
                  }
                  e.target.value = '';
                }} />

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button style={{ ...styles.addButton, padding: '8px 14px', fontSize: 12, background: '#0078D4' }}
                  onClick={() => { csvImportRef.current.dataset.importType = 'verlof'; csvImportRef.current.click(); }}>
                  📋 Import verlof/ziek CSV
                </button>
                <button style={{ ...styles.addButton, padding: '8px 14px', fontSize: 12, background: '#E86E1C' }}
                  onClick={() => { csvImportRef.current.dataset.importType = 'overuren'; csvImportRef.current.click(); }}>
                  ⏰ Import overuren CSV
                </button>
                <button style={{ ...styles.addButton, padding: '8px 14px', fontSize: 12, background: '#8764B8' }}
                  onClick={() => { csvImportRef.current.dataset.importType = 'flex'; csvImportRef.current.click(); }}>
                  ⚡ Import flex planning CSV
                </button>
              </div>

              {/* Import preview */}
              {csvImport && (
                <div style={{ marginTop: 14, background: '#fff', borderRadius: 8, border: '1px solid #edebe9', padding: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>
                    Preview: {csvImport.type === 'verlof' ? '📋 Verlof/Ziek' : csvImport.type === 'overuren' ? '⏰ Overuren' : '⚡ Flex planning'}
                  </div>

                  {csvImport.errors.length > 0 && (
                    <div style={{ background: '#fde7e9', border: '1px solid #D13438', borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#D13438', marginBottom: 4 }}>❌ Fouten ({csvImport.errors.length})</div>
                      {csvImport.errors.slice(0, 10).map((err, i) => (
                        <div key={i} style={{ fontSize: 11, color: '#D13438', lineHeight: 1.5 }}>{err}</div>
                      ))}
                      {csvImport.errors.length > 10 && <div style={{ fontSize: 11, color: '#D13438' }}>...en nog {csvImport.errors.length - 10} fouten</div>}
                    </div>
                  )}

                  {csvImport.warnings.length > 0 && (
                    <div style={{ background: '#FFF4CE', border: '1px solid #FFB900', borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#7A6400', marginBottom: 4 }}>⚠️ Waarschuwingen ({csvImport.warnings.length})</div>
                      {csvImport.warnings.slice(0, 5).map((w, i) => (
                        <div key={i} style={{ fontSize: 11, color: '#7A6400', lineHeight: 1.5 }}>{w}</div>
                      ))}
                    </div>
                  )}

                  {csvImport.records.length > 0 && (
                    <>
                      <div style={{ fontSize: 12, color: '#107C10', fontWeight: 600, marginBottom: 8 }}>
                        ✅ {csvImport.records.length} regels klaar voor import
                      </div>
                      <div style={{ maxHeight: 200, overflow: 'auto', marginBottom: 10 }}>
                        <div className="r-table-wrap">
                        <table style={{ ...styles.table, fontSize: 11 }}>
                          <thead>
                            <tr>
                              {csvImport.type === 'verlof' && <><th style={styles.th}>Medewerker</th><th style={styles.th}>Type</th><th style={styles.th}>Status</th><th style={styles.th}>Periode</th><th style={styles.th}>Uren</th></>}
                              {csvImport.type === 'overuren' && <><th style={styles.th}>Medewerker</th><th style={styles.th}>Datum</th><th style={styles.th}>Uren</th><th style={styles.th}>Status</th></>}
                              {csvImport.type === 'flex' && <><th style={styles.th}>Flexkracht</th><th style={styles.th}>Datum</th><th style={styles.th}>Uren</th><th style={styles.th}>Locatie</th></>}
                            </tr>
                          </thead>
                          <tbody>
                            {csvImport.records.slice(0, 20).map((r, i) => (
                              <tr key={i} style={styles.tr}>
                                {csvImport.type === 'verlof' && <><td style={styles.td}>{r.medewerkerNaam}</td><td style={styles.td}>{r.type}</td><td style={styles.td}>{r.status}</td><td style={styles.td}>{fmtDatum(r.startDatum)} t/m {fmtDatum(r.eindDatum)}</td><td style={styles.td}>{r.uren}u</td></>}
                                {csvImport.type === 'overuren' && <><td style={styles.td}>{r.medewerkerNaam}</td><td style={styles.td}>{fmtDatum(r.datum)}</td><td style={styles.td}>{r.uren}u</td><td style={styles.td}>{r.status}</td></>}
                                {csvImport.type === 'flex' && <><td style={styles.td}>{r.flexkrachtNaam}</td><td style={styles.td}>{fmtDatum(r.datum)}</td><td style={styles.td}>{r.uren}u</td><td style={styles.td}>{r.locatie || '—'}</td></>}
                              </tr>
                            ))}
                            {csvImport.records.length > 20 && (
                              <tr><td colSpan={5} style={{ ...styles.td, textAlign: 'center', color: '#605e5c' }}>...en nog {csvImport.records.length - 20} regels</td></tr>
                            )}
                          </tbody>
                        </table>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button style={{ ...styles.saveBtn, padding: '8px 18px' }}
                          disabled={csvImporting}
                          onClick={async () => {
                            if (!window.confirm(`Weet je zeker dat je ${csvImport.records.length} regels wilt importeren? Dit kan niet ongedaan worden.`)) return;
                            setCsvImporting(true);
                            try {
                              let result;
                              if (csvImport.type === 'verlof') {
                                result = await batchImportVerlof(csvImport.records);
                              } else if (csvImport.type === 'overuren') {
                                result = await batchImportOveruren(csvImport.records);
                              } else if (csvImport.type === 'flex') {
                                result = await batchImportFlexPlanning(csvImport.records);
                              }
                              setCsvImportResult(result);
                              setCsvImport(null);
                            } catch (err) {
                              setCsvImportResult({ success: 0, errors: [err.message] });
                            } finally {
                              setCsvImporting(false);
                            }
                          }}>
                          {csvImporting ? '⏳ Importeren...' : `✅ Importeer ${csvImport.records.length} regels`}
                        </button>
                        <button style={{ ...styles.cancelBtn, padding: '8px 14px' }} onClick={() => setCsvImport(null)}>
                          Annuleren
                        </button>
                      </div>
                    </>
                  )}

                  {csvImport.records.length === 0 && csvImport.errors.length === 0 && (
                    <div style={{ fontSize: 12, color: '#a19f9d' }}>Geen geldige regels gevonden in het bestand.</div>
                  )}
                </div>
              )}

              {/* Import result */}
              {csvImportResult && (
                <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8, border: '1px solid',
                  background: csvImportResult.errors?.length > 0 ? '#fde7e9' : '#dff6dd',
                  borderColor: csvImportResult.errors?.length > 0 ? '#D13438' : '#a7e3a5',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: csvImportResult.errors?.length > 0 ? '#D13438' : '#107C10' }}>
                    ✅ {csvImportResult.success} regels succesvol geïmporteerd
                    {csvImportResult.errors?.length > 0 && ` · ❌ ${csvImportResult.errors.length} fouten`}
                  </div>
                  {csvImportResult.errors?.slice(0, 5).map((e, i) => (
                    <div key={i} style={{ fontSize: 11, color: '#D13438', marginTop: 2 }}>{e}</div>
                  ))}
                </div>
              )}
            </div>

            {/* JSON Import (backup restore) */}
            <div style={{ background: '#faf9f8', border: '1px solid #edebe9', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#605e5c', marginBottom: 8 }}>📂 JSON Backup</div>
              <input type="file" accept=".json" ref={importRef} style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    setImportStatus({ type: 'loading', msg: 'Bestand lezen...' });
                    const data = await leesImportBestand(file);
                    const summary = [];
                    if (data.medewerkers) summary.push(`${data.medewerkers.length} medewerkers`);
                    if (data.verlofaanvragen) summary.push(`${data.verlofaanvragen.length} verlofaanvragen`);
                    if (data.overuren) summary.push(`${data.overuren.length} overuren`);
                    setImportStatus({ type: 'preview', msg: `${summary.join(', ')}`, data, exportDatum: data.exportDatum });
                  } catch (err) {
                    setImportStatus({ type: 'error', msg: err.message });
                  }
                  e.target.value = '';
                }} />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button style={{ ...styles.cancelBtn, padding: '6px 14px', fontSize: 12 }}
                  onClick={() => importRef.current?.click()}>
                  📂 Kies JSON bestand
                </button>
                {importStatus && importStatus.type === 'preview' && (
                  <span style={{ fontSize: 12, color: '#107C10', fontWeight: 600 }}>✅ {importStatus.msg}</span>
                )}
                {importStatus && importStatus.type === 'error' && (
                  <span style={{ fontSize: 12, color: '#D13438', fontWeight: 600 }}>❌ {importStatus.msg}</span>
                )}
              </div>
            </div>

            <div style={{ fontSize: 12, color: '#a19f9d', lineHeight: 1.5 }}>
              💡 <strong>Tip:</strong> Download eerst een template CSV, vul deze in met historische mutaties in Excel, en importeer daarna.
              Medewerkers worden automatisch gekoppeld op naam. Datumnotatie: dd-mm-jjjj of jjjj-mm-dd.
            </div>
          </div>

          {/* ===== BACKUP & HERSTEL ===== */}
          <div style={{ ...styles.card, border: '2px solid #D83B01' }}>
            <h3 style={{ ...styles.cardTitle, color: '#D83B01' }}>🔒 Backup & Herstel</h3>
            <p style={{ fontSize: 13, color: '#605e5c', marginBottom: 16, lineHeight: 1.6 }}>
              Download een volledige backup van alle data (medewerkers, verlof, overuren, instellingen).
              Bij calamiteiten kan deze backup worden hersteld.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <button style={{ ...styles.saveBtn, padding: '10px 20px', fontSize: 13, background: '#D83B01' }}
                onClick={async () => {
                  try {
                    setProcessing(true);
                    const result = await downloadBackup(true);
                    alert(`✅ Volledige backup gedownload:\n${result.filename}\n${result.records} records · ${(result.size / 1024).toFixed(0)} KB`);
                  } catch (err) { alert('❌ Backup mislukt: ' + err.message); }
                  finally { setProcessing(false); }
                }} disabled={processing}>
                {processing ? '⏳ Bezig...' : '💾 Volledige backup downloaden'}
              </button>
              <button style={{ ...styles.cancelBtn, padding: '10px 20px', fontSize: 13 }}
                onClick={async () => {
                  try {
                    setProcessing(true);
                    const result = await downloadBackup(false);
                    alert(`✅ Template backup gedownload (zonder medewerkerdata):\n${result.filename}\n${result.records} records · ${(result.size / 1024).toFixed(0)} KB`);
                  } catch (err) { alert('❌ Template export mislukt: ' + err.message); }
                  finally { setProcessing(false); }
                }} disabled={processing}>
                📦 Template export (white-label)
              </button>
            </div>

            <div style={{ background: '#FFF4CE', border: '1px solid #FFB900', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#7A6400', marginBottom: 12 }}>
              ⚠️ <strong>Herstel overschrijft bestaande data.</strong> Gebruik dit alleen bij noodgevallen of bij het opzetten van een nieuwe installatie.
            </div>
            <input type="file" accept=".json" id="backupRestore" style={{ display: 'none' }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (!window.confirm(`⚠️ WAARSCHUWING\n\nDit herstelt data uit "${file.name}".\nBestaande records met dezelfde ID worden overschreven.\n\nDoorgaan?`)) { e.target.value = ''; return; }
                try {
                  setProcessing(true);
                  const result = await herstelBackup(file, (p) => console.log(p.fase, p.verwerkt + '/' + p.totaal));
                  alert(`✅ Backup hersteld!\n${result.verwerkt} van ${result.totaal} records verwerkt.\n\nHerlaad de pagina om de wijzigingen te zien.`);
                  window.location.reload();
                } catch (err) { alert('❌ Herstel mislukt: ' + err.message); }
                finally { setProcessing(false); e.target.value = ''; }
              }} />
            <button style={{ ...styles.cancelBtn, padding: '8px 16px', fontSize: 12 }}
              onClick={() => document.getElementById('backupRestore').click()} disabled={processing}>
              📂 Backup herstellen (JSON)
            </button>
          </div>

          {/* ===== API KEYS & BEVEILIGING ===== */}
          <div style={{ ...styles.card, border: '2px solid #8764B8' }}>
            <h3 style={{ ...styles.cardTitle, color: '#8764B8' }}>🔑 API Keys & Beveiliging</h3>

            <div style={styles.settingsList}>
              <div style={styles.settingItem} className="r-setting-item">
                <div><div style={styles.settingLabel}>Firebase / Firestore</div><div style={styles.settingDesc}>Database en hosting — Spark plan (gratis) of Blaze plan (pay-as-you-go). Geen vervaldatum op API key.</div></div>
                <div style={{ ...styles.settingValue, color: '#107C10' }}>✅ Geen limiet</div>
              </div>
              <div style={styles.settingItem} className="r-setting-item">
                <div><div style={styles.settingLabel}>Vercel Hosting</div><div style={styles.settingDesc}>Frontend hosting — Hobby plan (gratis) of Pro plan. Geen API key nodig, deployment via Git.</div></div>
                <div style={{ ...styles.settingValue, color: '#107C10' }}>✅ Geen limiet</div>
              </div>
              <div style={styles.settingItem} className="r-setting-item">
                <div><div style={styles.settingLabel}>FCM Push Notifications</div><div style={styles.settingDesc}>Firebase Cloud Messaging — gratis, onbeperkt. Server key in Vercel env (optioneel).</div></div>
                <div style={{ ...styles.settingValue, color: '#107C10' }}>✅ Gratis onbeperkt</div>
              </div>
              <div style={styles.settingItem} className="r-setting-item">
                <div><div style={styles.settingLabel}>Microsoft Graph (Outlook)</div><div style={styles.settingDesc}>Agenda sync — Azure AD App Registration. Client ID/Tenant ID, geen kosten. Token verloopt maar wordt automatisch ververst.</div></div>
                <div style={{ ...styles.settingValue, color: '#0078D4' }}>ℹ️ Optioneel</div>
              </div>
            </div>

            <div style={{ marginTop: 16, background: '#f0fff0', border: '1px solid #a7e3a5', borderRadius: 10, padding: 16, fontSize: 13, lineHeight: 1.8, color: '#323130' }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>🔒 Beveiligingsstatus</div>
              <div>✅ Wachtwoorden worden gehasht opgeslagen (SHA-256 + salt)</div>
              <div>✅ Geen API keys met vervaldatum — app blijft altijd bereikbaar</div>
              <div>✅ Geen gevoelige data in frontend code (enkel Firebase project-ID)</div>
              <div>✅ Firebase API key is publiek-veilig (beperkt tot dit project)</div>
              <div>⚠️ <strong>Aanbeveling:</strong> Stel Firestore Security Rules in via Firebase Console voor productiegebruik</div>
              <div>⚠️ <strong>Aanbeveling:</strong> Wijzig het standaard wachtwoord van de beheerder</div>
            </div>
          </div>

          {/* ===== WHITE-LABEL SETUP ===== */}
          <div style={{ ...styles.card, border: '2px solid #605e5c' }}>
            <h3 style={{ ...styles.cardTitle, color: '#605e5c' }}>🏷️ White-label: App doorverkopen</h3>
            <p style={{ fontSize: 13, color: '#605e5c', marginBottom: 16, lineHeight: 1.6 }}>
              Om de app te verkopen aan een ander bedrijf, maak je een kopie van de GitHub repository en zet je voor de klant eigen API keys op.
            </p>
            <div style={{ background: '#faf9f8', borderRadius: 10, padding: 16, fontSize: 13, lineHeight: 2, color: '#323130' }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>📋 Stappen voor nieuwe klant</div>
              <div><strong>1. GitHub:</strong> Fork of kopieer de repository naar een nieuw account/repo</div>
              <div><strong>2. Firebase:</strong> Maak een nieuw Firebase project aan op <em>console.firebase.google.com</em></div>
              <div style={{ paddingLeft: 16 }}>• Firestore Database aanmaken (production mode)</div>
              <div style={{ paddingLeft: 16 }}>• Kopieer <code>apiKey</code>, <code>authDomain</code>, <code>projectId</code>, <code>storageBucket</code>, <code>messagingSenderId</code>, <code>appId</code></div>
              <div style={{ paddingLeft: 16 }}>• Plak in <code>src/config/firebase.js</code></div>
              <div><strong>3. Vercel:</strong> Importeer de repo in Vercel, kies het juiste Framework Preset (Create React App)</div>
              <div style={{ paddingLeft: 16 }}>• Optioneel: stel <code>FCM_SERVER_KEY</code> in als Environment Variable voor push notificaties</div>
              <div><strong>4. Outlook (optioneel):</strong> Registreer een Azure AD App voor agenda-sync</div>
              <div><strong>5. Bedrijfsgegevens:</strong> Pas aan in <code>src/config/sharepointConfig.js</code></div>
              <div style={{ paddingLeft: 16 }}>• Bedrijfsnaam, logo, kleuren, rollen, standaard contracturen</div>
              <div><strong>6. Template import:</strong> Upload de "Template export (white-label)" backup hierboven</div>
              <div style={{ paddingLeft: 16 }}>• Dit zet verloftypen, feestdagen, vakantieperioden klaar zonder medewerkerdata</div>
              <div><strong>7. Eerste beheerder:</strong> De app maakt automatisch een beheerder aan bij eerste gebruik</div>
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #edebe9', fontWeight: 700 }}>💰 Aanbevolen abonnementsvorm</div>
              <div>• Setup fee (eenmalig): Firebase + Vercel + bedrijfsinstellingen</div>
              <div>• Maandelijks: hosting + onderhoud + support</div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'beschikbaarheid' && (
        <div style={styles.section}>
          <div style={styles.card}>
            <BeschikbaarheidDashboard />
          </div>
        </div>
      )}

      {activeTab === 'diagnose' && (
        <div style={styles.section}>
          <div style={styles.card}>
            <SystemCheck onNavigate={(tab) => setActiveTab(tab)} />
          </div>
        </div>
      )}

      {/* ==================== PDF DIALOG ==================== */}
      {pdfDialog && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setPdfDialog(null)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 500, width: '100%', boxShadow: '0 16px 48px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 16 }}>📄 PDF Totaaloverzicht</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#605e5c' }}>Van datum</label>
                <input type="date" value={pdfDialog.vanDatum} onChange={e => setPdfDialog({ ...pdfDialog, vanDatum: e.target.value })}
                  style={{ width: '100%', padding: 8, border: '1px solid #edebe9', borderRadius: 8, fontSize: 13 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#605e5c' }}>Tot datum</label>
                <input type="date" value={pdfDialog.totDatum} onChange={e => setPdfDialog({ ...pdfDialog, totDatum: e.target.value })}
                  style={{ width: '100%', padding: 8, border: '1px solid #edebe9', borderRadius: 8, fontSize: 13 }} />
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#605e5c' }}>Medewerker</label>
              <select value={pdfDialog.medewerkerId} onChange={e => setPdfDialog({ ...pdfDialog, medewerkerId: e.target.value })}
                style={{ width: '100%', padding: 8, border: '1px solid #edebe9', borderRadius: 8, fontSize: 13, marginTop: 4 }}>
                <option value="alle">Alle medewerkers</option>
                {medewerkers.filter(m => (m.Status || 'actief') !== 'uitdienst' && m.Rol !== 'flexkracht')
                  .sort((a, b) => a.Naam.localeCompare(b.Naam))
                  .map(m => <option key={m.Id} value={m.Id}>{m.Naam}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 20, background: '#f9f8f7', borderRadius: 10, padding: '12px 16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#323130', cursor: 'pointer' }}>
                <input type="checkbox" checked={pdfDialog.inclMutaties || false}
                  onChange={e => setPdfDialog({ ...pdfDialog, inclMutaties: e.target.checked })} />
                📋 Inclusief mutatielogboek
              </label>
              <div style={{ fontSize: 11, color: '#a19f9d', marginTop: 4, paddingLeft: 24 }}>
                Toont per medewerker alle individuele verlofaanvragen, ziekmeldingen en overuren in de geselecteerde periode.
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setPdfDialog(null)} style={{ padding: '10px 20px', border: '1px solid #edebe9', borderRadius: 8, background: '#fff', fontSize: 13, cursor: 'pointer' }}>Annuleren</button>
              <button onClick={() => {
                try {
                  const selectedMw = pdfDialog.medewerkerId === 'alle'
                    ? medewerkers.filter(m => m.Rol !== 'flexkracht' && (m.Status || 'actief') !== 'uitdienst')
                    : medewerkers.filter(m => m.Id === pdfDialog.medewerkerId);
                  const doc = genereerTotaaloverzichtPDF(selectedMw, verlofaanvragen, overuren, verlofTypes, feestdagen, pdfDialog.vanDatum, pdfDialog.totDatum, { inclMutaties: pdfDialog.inclMutaties });
                  const blob = doc.output('blob');
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  const label = pdfDialog.medewerkerId === 'alle' ? 'totaaloverzicht' : selectedMw[0]?.Naam?.replace(/\s+/g, '-').toLowerCase();
                  a.download = `slaaploods-${label}-${pdfDialog.vanDatum}-${pdfDialog.totDatum}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  setPdfDialog(null);
                } catch (err) {
                  console.error('PDF fout:', err);
                  alert('PDF genereren mislukt: ' + err.message);
                }
              }} style={{ padding: '10px 20px', border: 'none', borderRadius: 8, background: '#0078D4', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>📄 Genereren</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { display: 'flex', flexDirection: 'column', gap: 24, maxWidth: '100%', boxSizing: 'border-box' },
  tabs: { display: 'flex', gap: 4, borderBottom: '2px solid #edebe9', paddingBottom: 0, flexWrap: 'wrap', position: 'sticky', top: 56, background: '#f5f5f5', zIndex: 15, paddingTop: 8, marginLeft: 'clamp(-12px, -24px, -24px)', marginRight: 'clamp(-12px, -24px, -24px)', paddingLeft: 24, paddingRight: 24 },
  tab: { padding: '10px 16px', border: 'none', background: 'none', fontSize: 13, fontWeight: 600, color: '#605e5c', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -2, whiteSpace: 'nowrap' },
  tabActive: { color: '#E86E1C', borderBottom: '2px solid #E86E1C' },
  section: { display: 'flex', flexDirection: 'column', gap: 20, maxWidth: '100%' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 },
  statCard: { background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column' },
  statNumber: { fontSize: 36, fontWeight: 800, color: '#1a1a2e', lineHeight: 1 },
  statLabel: { fontSize: 12, color: '#605e5c', marginTop: 4, fontWeight: 500 },
  card: { background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' },
  cardTitle: { fontSize: 18, fontWeight: 600, color: '#1a1a2e', marginBottom: 16 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 600, color: '#605e5c', borderBottom: '2px solid #edebe9', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f3f2f1' },
  td: { padding: '10px 12px', fontSize: 13, color: '#323130' },
  roleBadge: { fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20 },
  hint: { fontSize: 13, color: '#a19f9d', padding: '12px 16px', background: '#faf9f8', borderRadius: 8, marginBottom: 16 },
  hintInline: { fontSize: 12, color: '#8a8886', background: '#faf9f8', padding: '8px 12px', borderRadius: 8, marginBottom: 12, lineHeight: 1.5 },
  addButton: { background: '#E86E1C', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  editRow: { background: '#faf9f8', borderRadius: 8, padding: 16, marginBottom: 16, border: '1px solid #edebe9' },
  editGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 12 },
  editLabel: { display: 'block', fontSize: 12, fontWeight: 600, color: '#605e5c', marginBottom: 4 },
  editInput: { width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #c8c6c4', fontSize: 13, color: '#323130' },
  editActions: { display: 'flex', gap: 8 },
  actionRow: { display: 'flex', gap: 6 },
  editBtn: { padding: '6px 12px', borderRadius: 6, border: '1px solid #edebe9', background: '#fff', fontSize: 13, cursor: 'pointer' },
  deleteBtn: { width: 32, height: 32, borderRadius: 8, border: '1px solid #edebe9', background: '#fff', fontSize: 14, cursor: 'pointer' },
  saveBtn: { padding: '6px 14px', borderRadius: 6, border: 'none', background: '#107C10', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  cancelBtn: { padding: '6px 14px', borderRadius: 6, border: '1px solid #c8c6c4', background: '#fff', color: '#605e5c', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  settingsList: { display: 'flex', flexDirection: 'column' },
  settingItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '16px 0', borderBottom: '1px solid #f3f2f1', gap: 16, flexWrap: 'wrap' },
  settingLabel: { fontSize: 14, fontWeight: 600, color: '#323130' },
  settingDesc: { fontSize: 12, color: '#a19f9d', marginTop: 2 },
  settingValue: { fontSize: 14, fontWeight: 600, color: '#0078D4', whiteSpace: 'nowrap', flexShrink: 0 },
  settingTags: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  tag: { fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 4 },
  noAccess: { textAlign: 'center', padding: 80, background: '#fff', borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
};

export default AdminPanel;
